import React, { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  closestCenter,
  useDraggable,
  useDroppable,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useBookingSignal } from '../hooks/useBookingSignal';
import { FeedbackDialog } from '../components/FeedbackDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CheckoutDiscount } from '../components/CheckoutDiscount';
import { RebookStep } from '../components/RebookStep';
import { DISCOUNT_LABELS, previewDiscount, type DiscountChoice } from '../lib/discounts';
import {
  isAuMobile,
  reliabilityText,
  useCustomerReliability,
  type CustomerReliability,
} from '../lib/reliability';

interface Booking {
  id: string;
  barber_id: number;
  location_id: number;
  start_time: string;
  end_time: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  is_guest?: boolean;
  is_product_only?: boolean;
  source?: string;
  services?: {
    name: string;
    duration_minutes?: number;
    duration?: number;
    price: number | string;
    /** booking_service row id + discount on it (migration 054). */
    booking_service_id?: number;
    discount?: number | string;
  }[];
  status: string;
  discount_type?: 'loyalty' | 'voucher' | null;
  product_discount?: number | string;
}

interface BarberBreak {
  id: number;
  barber_id: number;
  start_time: string;
  end_time: string;
  type?: string;
}

const BRISBANE_TZ = 'Australia/Brisbane';

function toBrisbaneHoursMinutes(isoString: string): { hours: number; minutes: number } {
  const d = new Date(isoString);
  const parts = d
    .toLocaleString('en-AU', {
      timeZone: BRISBANE_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .split(':');
  return { hours: parseInt(parts[0], 10), minutes: parseInt(parts[1], 10) };
}

function formatBrisbaneTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString('en-AU', {
    timeZone: BRISBANE_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function getTodayBrisbane(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRISBANE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function addDays(dateStr: string, days: number): string {
  // Parse as YYYY-MM-DD in Brisbane, add days, return YYYY-MM-DD
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateDisplay(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
}

/** Convert Brisbane local time to UTC ISO string */
function brisbaneDateToISO(dateStr: string, timeStr: string): string {
  // Build the UTC instant that corresponds to {dateStr, timeStr} interpreted
  // as Brisbane wall-clock time, INDEPENDENT of the browser's own timezone.
  //
  // Previous implementation used an 'en-AU' Intl formatter without
  // `hour12: false`. For any browser TZ where Brisbane noon falls in PM
  // (UTC, WA/Perth, Bali/Makassar — anywhere west of Brisbane) the
  // formatter returned `hour: "02"` with a separate `dayPeriod: "pm"`
  // token the code never read. The "02" got parsed as 24-hour, the
  // computed offset was off by 12h, and a booking dragged to 10:00 am
  // Brisbane on 3 Jul came back as 10:00 pm Brisbane on 2 Jul (or
  // similar) — so the booking "disappeared" from the day's view after
  // refetch.
  //
  // Repro the user hit in production: drag a booking on 2026-07-03 to a
  // new slot — the booking vanished from the day's calendar. Same bug
  // class as the May 18 rosters regression (fixed in Rosters.tsx).
  //
  // Lock the probe formatter to 24-hour via `en-GB` + `hour12: false`.
  // TODO: extract this into a shared admin helper so this fix doesn't
  // have to be applied to a third copy if someone adds another caller.
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const testDate = new Date(year, month - 1, day, 12, 0, 0);
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BRISBANE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(testDate);
  const partMap: Record<string, string> = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') partMap[part.type] = part.value;
  });
  // en-GB + hour12:false can return "24" for midnight on some engines —
  // normalise to "00" so the Date constructor parses it.
  const safeHour = partMap.hour === '24' ? '00' : partMap.hour;
  const localDate = new Date(
    `${partMap.year}-${partMap.month}-${partMap.day}T${safeHour}:${partMap.minute}:${partMap.second}`,
  );
  const offsetMs = testDate.getTime() - localDate.getTime();
  const targetLocal = new Date(year, month - 1, day, hour, minute, 0);
  const targetUTC = new Date(targetLocal.getTime() + offsetMs);
  return targetUTC.toISOString();
}

// Styles
const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-2)',
  flexWrap: 'wrap',
  gap: 'var(--spacing-2)',
};
const btnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontWeight: '500',
};
const btnAccent: React.CSSProperties = {
  ...btnStyle,
  backgroundColor: 'var(--color-accent)',
  border: '1px solid var(--color-accent)',
  color: '#1e1e1e',
  fontWeight: '600',
};
const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-sm)',
};
const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-base)',
  width: '100%',
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: '600',
  marginBottom: 'var(--spacing-1)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const SLOT_HEIGHT = 28;

type FeedbackDialogState = {
  tone?: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  actionLabel?: string;
};

type DragState = {
  activeId: string | null;
  overId: string | null;
};

/** Convert YYYY-MM-DD to ISO day_of_week (1=Mon..7=Sun) */
function getDayOfWeek(dateStr: string): number {
  const d = new Date(dateStr + 'T12:00:00');
  const jsDay = d.getDay(); // 0=Sun
  return jsDay === 0 ? 7 : jsDay; // 1=Mon..7=Sun
}

/** Parse "HH:MM:SS" or "HH:MM" to hours/minutes */
function parseTime(t: string): { hours: number; minutes: number } {
  const [h, m] = t.split(':').map(Number);
  return { hours: h, minutes: m || 0 };
}

function getBreakFeedback(message?: string): FeedbackDialogState {
  switch (message) {
    case 'Only one break per barber is allowed per day':
      return {
        tone: 'warning',
        title: 'Break already scheduled',
        message:
          'Each barber can only have one break per day. Move or edit the existing break instead.',
      };
    case 'Break must be at least 30 minutes after day start and end at least 30 minutes before day finish':
      return {
        tone: 'warning',
        title: 'Break is outside the allowed window',
        message:
          'Breaks need to start at least 30 minutes after the day begins and finish at least 30 minutes before it ends.',
      };
    case 'Cannot create break when barber has no shift on this day':
      return {
        tone: 'warning',
        title: 'No shift scheduled',
        message: 'This barber does not have a shift on this day, so a break cannot be added yet.',
      };
    case 'Cannot create break on a closed day':
      return {
        tone: 'warning',
        title: 'Location is closed',
        message: 'Breaks can only be added on days when the selected location is open.',
      };
    case 'Break end time must be after start time':
      return {
        tone: 'warning',
        title: 'Break times need adjusting',
        message: 'The break end time needs to be later than the start time.',
      };
    default:
      return {
        tone: 'error',
        title: 'Could not save break',
        message: message || 'Something went wrong while saving the break. Please try again.',
      };
  }
}

/* ─── Droppable Cell ─── */
function DroppableCell({
  id,
  onClick,
  style,
  occupiedDropTarget,
  disabled,
}: {
  id: string;
  onClick: () => void;
  style: React.CSSProperties;
  occupiedDropTarget?: boolean;
  disabled?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      style={{
        ...style,
        backgroundColor: occupiedDropTarget
          ? 'rgba(217, 178, 86, 0.16)'
          : isOver && !disabled
            ? 'rgba(59, 155, 120, 0.16)'
            : style.backgroundColor,
        boxShadow: occupiedDropTarget
          ? 'inset 0 0 0 2px rgba(217, 178, 86, 0.5)'
          : isOver && !disabled
            ? 'inset 0 0 0 2px rgba(59, 155, 120, 0.35)'
            : undefined,
        transition: 'background-color 120ms ease, box-shadow 120ms ease',
      }}
    />
  );
}

/**
 * Compute side-by-side layout for overlapping bookings within a barber column.
 * Returns a map of booking id → { index, total } where index is the column
 * position (0-based) and total is how many columns in the overlap group.
 */
function computeOverlapLayout(
  barberBookings: Booking[],
  getPos: (b: Booking) => { top: number; height: number },
): Map<string, { index: number; total: number }> {
  const layout = new Map<string, { index: number; total: number }>();
  if (barberBookings.length === 0) return layout;

  // Sort by top position, then by height descending (longer first)
  const sorted = [...barberBookings].sort(
    (a, b) => getPos(a).top - getPos(b).top || getPos(b).height - getPos(a).height,
  );

  // Build overlap clusters — groups of bookings that transitively overlap
  const positions = sorted.map((bk) => {
    const p = getPos(bk);
    return { id: bk.id, startY: p.top, endY: p.top + p.height };
  });

  const clusters: (typeof positions)[] = [];
  let current: typeof positions = [];
  let clusterEnd = -Infinity;

  for (const p of positions) {
    if (current.length === 0 || p.startY < clusterEnd) {
      current.push(p);
      clusterEnd = Math.max(clusterEnd, p.endY);
    } else {
      clusters.push(current);
      current = [p];
      clusterEnd = p.endY;
    }
  }
  if (current.length > 0) clusters.push(current);

  // Assign columns within each cluster independently
  for (const cluster of clusters) {
    const columns: { id: string; endY: number }[][] = [];
    for (const item of cluster) {
      let placed = false;
      for (let col = 0; col < columns.length; col++) {
        const lastInCol = columns[col][columns[col].length - 1];
        if (lastInCol.endY <= item.startY) {
          columns[col].push({ id: item.id, endY: item.endY });
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([{ id: item.id, endY: item.endY }]);
      }
    }
    const total = columns.length;
    for (let col = 0; col < columns.length; col++) {
      for (const item of columns[col]) {
        layout.set(item.id, { index: col, total });
      }
    }
  }

  return layout;
}

/* ─── Draggable Booking Block ─── */
function groupServices(
  services: any[],
): { name: string; count: number; duration: number; price: number }[] {
  const map = new Map<string, { name: string; count: number; duration: number; price: number }>();
  for (const s of services) {
    const key = s.name;
    const existing = map.get(key);
    const dur = s.duration_minutes || s.duration || 0;
    const price = Number(s.price) || 0;
    if (existing) {
      existing.count++;
      existing.duration += dur;
      existing.price += price;
    } else {
      map.set(key, { name: key, count: 1, duration: dur, price });
    }
  }
  return Array.from(map.values());
}

function DraggableBooking({
  booking,
  top,
  height,
  statusColor,
  onClick,
  colIndex,
  colTotal,
  dimmed,
  occupiedDrop,
  warning,
}: {
  booking: Booking;
  top: number;
  height: number;
  statusColor: { bg: string; border: string; text: string };
  onClick: () => void;
  colIndex?: number;
  colTotal?: number;
  dimmed?: boolean;
  occupiedDrop?: boolean;
  /** Customer who keeps missing or moving bookings — shows ⚠ before the name. */
  warning?: CustomerReliability | null;
}) {
  const canDrag = booking.status === 'BOOKED';
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: booking.id,
    disabled: !canDrag,
  });
  const leftPct = colTotal && colTotal > 1 ? `${((colIndex || 0) / colTotal) * 100}%` : '2px';
  const widthPct = colTotal && colTotal > 1 ? `${100 / colTotal}%` : undefined;
  const dragScale = isDragging ? 0.88 : occupiedDrop ? 0.86 : 1;
  const dragOpacity = dimmed ? 0.3 : isDragging ? 0.58 : occupiedDrop ? 0.42 : 1;
  const dragStyle: React.CSSProperties = {
    position: 'absolute',
    top: `${top}px`,
    height: `${height}px`,
    left: leftPct,
    right: widthPct ? undefined : '2px',
    width: widthPct,
    backgroundColor: statusColor.bg,
    backgroundImage:
      booking.status === 'COMPLETED'
        ? 'repeating-linear-gradient(135deg, rgba(40,167,69,0.18), rgba(40,167,69,0.18) 6px, transparent 6px, transparent 12px)'
        : undefined,
    borderLeft: `4px solid ${statusColor.border}`,
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
    overflow: 'hidden',
    cursor: !canDrag ? 'pointer' : isDragging ? 'grabbing' : 'grab',
    color: statusColor.text,
    fontWeight: '500',
    zIndex: isDragging ? 50 : dimmed ? 2 : 5,
    boxShadow: isDragging
      ? '0 10px 24px rgba(0,0,0,0.16)'
      : occupiedDrop
        ? '0 2px 10px rgba(0,0,0,0.08)'
        : dimmed
          ? 'none'
          : '0 1px 2px rgba(0,0,0,0.08)',
    opacity: dragOpacity,
    transform: isDragging
      ? transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${dragScale})`
        : undefined
      : occupiedDrop
        ? `scale(${dragScale})`
        : undefined,
    transformOrigin: 'top center',
    transition: isDragging ? undefined : 'box-shadow 0.2s, opacity 0.2s, transform 0.2s',
    pointerEvents: 'auto',
    backdropFilter: isDragging ? 'blur(1px)' : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      {...(canDrag ? listeners : {})}
      {...attributes}
      style={dragStyle}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {(() => {
        const grouped =
          booking.services && booking.services.length > 0 ? groupServices(booking.services) : [];
        const totalDuration = grouped.reduce((sum, g) => sum + g.duration, 0);
        const totalPrice = grouped.reduce((sum, g) => sum + g.price, 0);
        const serviceLines = grouped.map((g) => {
          const label = g.count > 1 ? `${g.name} x${g.count}` : g.name;
          return `${label} · ${g.duration}min · $${g.price.toFixed(0)}`;
        });
        const serviceTextShort =
          grouped
            .map((g) => {
              const label = g.count > 1 ? `${g.name} x${g.count}` : g.name;
              return `${label} ($${g.price.toFixed(0)})`;
            })
            .join(', ') || (booking.is_product_only ? 'Product Sale' : 'Service');
        const timeText = `${new Date(booking.start_time).toLocaleTimeString('en-AU', {
          timeZone: BRISBANE_TZ,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })} – ${new Date(booking.end_time).toLocaleTimeString('en-AU', {
          timeZone: BRISBANE_TZ,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}`;
        const summaryText = `${timeText} · ${totalDuration}min · $${totalPrice.toFixed(0)}`;
        const customerName =
          booking.customer_name || (booking.source === 'customer' ? 'Guest' : 'Walk-in');
        // ⭐ marker for requested-barber bookings — customer picked
        // this barber (not "Any Barber"), or admin created the booking
        // because someone specifically asked for them.
        const requestedStar = (booking as any).barber_requested ? '⭐ ' : '';
        const warningMark = warning ? '⚠️ ' : '';
        const nameTitle =
          [
            warning ? `⚠ ${reliabilityText(warning)}` : null,
            (booking as any).barber_requested ? 'Requested this barber' : null,
          ]
            .filter(Boolean)
            .join(' · ') || undefined;
        // height tiers: 1-line ≤30, 2-line ≤54, 3-line ≤80, 4+ >80
        const lines = height <= 30 ? 1 : height <= 54 ? 2 : height <= 80 ? 3 : 4;
        return (
          <>
            {lines === 1 ? (
              <div
                style={{
                  fontWeight: '600',
                  lineHeight: 1.3,
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={nameTitle}
              >
                {warningMark}
                {requestedStar}
                {customerName}{' '}
                <span style={{ fontWeight: 400, opacity: 0.75 }}>· {serviceTextShort}</span>
              </div>
            ) : (
              <div
                style={{
                  fontWeight: '600',
                  lineHeight: 1.3,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={nameTitle}
              >
                {warningMark}
                {requestedStar}
                {customerName}
              </div>
            )}
            {lines === 2 ? (
              <div
                style={{
                  lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  opacity: 0.75,
                  fontSize: '11px',
                }}
              >
                {serviceLines.join(', ')} · {timeText}
              </div>
            ) : lines >= 3 ? (
              <>
                {serviceLines.map((line, i) => (
                  <div
                    key={i}
                    style={{
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      opacity: 0.75,
                      fontSize: '11px',
                    }}
                  >
                    {line}
                  </div>
                ))}
                {lines >= 4 && (
                  <div
                    style={{
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      opacity: 0.6,
                      fontSize: '10px',
                      marginTop: 1,
                    }}
                  >
                    {summaryText}
                  </div>
                )}
              </>
            ) : null}
          </>
        );
      })()}
    </div>
  );
}

function DraggableBreak({
  brk,
  top,
  height,
  onCatchUpClick,
}: {
  brk: BarberBreak;
  top: number;
  height: number;
  onCatchUpClick?: (brk: BarberBreak) => void;
}) {
  const isCatchUp = brk.type === 'catchup';
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `break-${brk.id}`,
    disabled: isCatchUp,
  });

  return (
    <div
      ref={setNodeRef}
      {...(isCatchUp ? {} : { ...listeners, ...attributes })}
      onClick={
        isCatchUp
          ? (e) => {
              e.stopPropagation();
              onCatchUpClick?.(brk);
            }
          : undefined
      }
      style={{
        position: 'absolute',
        top: `${top}px`,
        height: `${height}px`,
        left: '2px',
        right: '2px',
        borderRadius: '4px',
        zIndex: isDragging ? 40 : 7,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: '700',
        letterSpacing: '0.04em',
        ...(isCatchUp
          ? {
              color: '#1a3a5c',
              background:
                'repeating-linear-gradient(135deg, rgba(59,130,246,0.3), rgba(59,130,246,0.3) 6px, rgba(147,197,253,0.2) 6px, rgba(147,197,253,0.2) 12px)',
              border: '2px dashed #3b82f6',
              cursor: 'pointer',
            }
          : {
              color: '#7a5400',
              background:
                'repeating-linear-gradient(135deg, rgba(217,178,86,0.45), rgba(217,178,86,0.45) 6px, rgba(240,210,130,0.25) 6px, rgba(240,210,130,0.25) 12px)',
              border: '2px dashed #c4a03a',
              cursor: isDragging ? 'grabbing' : 'grab',
            }),
        boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
        opacity: isDragging ? 0.85 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
      }}
      title={isCatchUp ? 'Click to edit or delete' : 'Drag break to move'}
    >
      {isCatchUp ? '⏱️ Catch Up' : '☕ Break'}
    </div>
  );
}

export function Calendar() {
  const { user } = useAuth();
  const isBarber = user?.role === 'BARBER';
  const [date, setDate] = useState(getTodayBrisbane);
  const isPastDate = date < getTodayBrisbane();
  const readOnly = isBarber && isPastDate;
  const [locationId, setLocationId] = useState<number | undefined>(user?.locationId);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [newBooking, setNewBooking] = useState<{
    barberId: number;
    time: string;
    isProductOnly?: boolean;
  } | null>(null);
  const [showProductSale, setShowProductSale] = useState(false);
  const [catchUpState, setCatchUpState] = useState<{
    barberId: number;
    barberName: string;
  } | null>(null);
  const [selectedCatchUp, setSelectedCatchUp] = useState<BarberBreak | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [interactionMode, setInteractionMode] = useState<'booking' | 'break'>('booking');
  const [feedbackDialog, setFeedbackDialog] = useState<FeedbackDialogState | null>(null);
  const [dragState, setDragState] = useState<DragState>({ activeId: null, overId: null });
  const searchRef = React.useRef<HTMLDivElement>(null);
  const gridScrollRef = React.useRef<HTMLDivElement>(null);
  const autoFocusedDateRef = React.useRef<string>('');
  const queryClient = useQueryClient();
  const [nowTick, setNowTick] = useState(() => Date.now());

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // Real-time booking updates via Firestore (falls back to 15s polling if Firebase not configured)
  useBookingSignal();

  // Fetch locations for admin filter
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
    enabled: !user?.locationId, // only fetch for admin
  });

  const effectiveLocationId = locationId || user?.locationId;

  // Cross-date search: debounce API call when search term has 2+ chars
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params: Record<string, string> = { search: searchTerm };
        if (effectiveLocationId) params.locationId = effectiveLocationId.toString();
        const results = await api.getBookings(params);
        setSearchResults(results.slice(0, 20));
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, effectiveLocationId]);

  // Close search dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch opening hours
  const { data: allOpeningHours = [] } = useQuery({
    queryKey: ['opening-hours'],
    queryFn: () => api.getOpeningHours(),
  });

  // Determine opening hours for the selected day/location
  const dayOfWeek = useMemo(() => getDayOfWeek(date), [date]);

  const todayHours = useMemo(() => {
    // Filter by location if selected
    const filtered = effectiveLocationId
      ? allOpeningHours.filter(
          (h: any) => h.location_id === effectiveLocationId && h.day_of_week === dayOfWeek,
        )
      : allOpeningHours.filter((h: any) => h.day_of_week === dayOfWeek);

    if (filtered.length === 0) return null; // Closed

    // If "All Locations", use the earliest open and latest close
    let earliestOpen = 24;
    let latestClose = 0;
    for (const h of filtered) {
      const open = parseTime(h.open_time);
      const close = parseTime(h.close_time);
      const openHour = open.hours + open.minutes / 60;
      const closeHour = close.hours + close.minutes / 60;
      if (openHour < earliestOpen) earliestOpen = openHour;
      if (closeHour > latestClose) latestClose = closeHour;
    }
    return {
      startHour: Math.floor(earliestOpen),
      startMinute: Math.round((earliestOpen % 1) * 60),
      endHour: Math.ceil(latestClose),
      endMinute: 0,
    };
  }, [allOpeningHours, effectiveLocationId, dayOfWeek]);

  // Fetch public holidays to close calendar on holiday dates
  const { data: publicHolidays = [] } = useQuery({
    queryKey: ['public-holidays'],
    queryFn: () => api.getPublicHolidays(),
  });

  const isPublicHoliday = useMemo(() => {
    return publicHolidays.some((h: any) => h.date === date);
  }, [publicHolidays, date]);

  const publicHolidayName = useMemo(() => {
    const h = publicHolidays.find((h: any) => h.date === date);
    return h ? h.name : null;
  }, [publicHolidays, date]);

  const isClosed = todayHours === null || isPublicHoliday;

  const { data: allBarbers = [] } = useQuery({
    queryKey: ['barbers', effectiveLocationId],
    queryFn: () => api.getBarbers(effectiveLocationId),
  });

  // Fetch shifts for the selected date to know which barbers are working
  const { data: dayShifts = [] } = useQuery({
    queryKey: ['rosters', date, effectiveLocationId],
    queryFn: () => {
      const params: Record<string, string> = {
        startDate: `${date}T00:00:00`,
        endDate: `${addDays(date, 1)}T00:00:00`,
      };
      if (effectiveLocationId) params.locationId = effectiveLocationId.toString();
      return api.getRosters(params);
    },
  });

  // Only show barbers who have a shift on this day.
  //
  // "All Locations" means all ENABLED locations: `locations` already excludes
  // any location switched off on the Locations page, so a disabled shop's
  // shifts (and therefore its barber columns and their bookings) never reach
  // the grid. Without this filter an unscoped roster fetch would still return
  // them and the disabled location would reappear here.
  const barbers = useMemo(() => {
    if (dayShifts.length === 0) return [];
    const enabledLocationIds = new Set(locations.map((l: any) => Number(l.id)));
    const visibleShifts = effectiveLocationId
      ? dayShifts
      : dayShifts.filter(
          (s: any) => s.location_id == null || enabledLocationIds.has(Number(s.location_id)),
        );
    const workingBarberIds = new Set(visibleShifts.map((s: any) => s.barber_id));
    return allBarbers.filter((b: any) => workingBarberIds.has(b.id));
  }, [allBarbers, dayShifts, locations, effectiveLocationId]);

  // Compute per-barber shift time ranges (in "HH:MM" format) for grey-out logic
  const barberShiftRanges = useMemo(() => {
    const ranges = new Map<number, { start: string; end: string }>();
    for (const shift of dayShifts) {
      const startBrisbane = formatBrisbaneTime(shift.start_time);
      const endBrisbane = formatBrisbaneTime(shift.end_time);
      const existing = ranges.get(shift.barber_id);
      if (!existing) {
        ranges.set(shift.barber_id, { start: startBrisbane, end: endBrisbane });
      } else {
        // Extend range if barber has multiple shifts
        if (startBrisbane < existing.start) existing.start = startBrisbane;
        if (endBrisbane > existing.end) existing.end = endBrisbane;
      }
    }
    return ranges;
  }, [dayShifts]);

  const bookingsQueryKey = useMemo(
    () => ['bookings', date, effectiveLocationId] as const,
    [date, effectiveLocationId],
  );

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: bookingsQueryKey,
    queryFn: () => {
      const params: Record<string, string> = { date };
      if (effectiveLocationId) params.locationId = effectiveLocationId.toString();
      return api.getBookings(params);
    },
  });

  const selectedBooking = useMemo(() => {
    if (!selectedBookingId) return null;
    return (bookings as Booking[]).find((b: Booking) => b.id === selectedBookingId) || null;
  }, [selectedBookingId, bookings]);

  // ⚠ on upcoming bookings of customers who keep missing or moving them.
  const reliabilityFor = useCustomerReliability(
    (bookings as Booking[]).filter((b) => b.status === 'BOOKED').map((b) => b.customer_phone),
  );

  const { data: services = [] } = useQuery({
    queryKey: ['services'],
    queryFn: api.getServices,
  });

  // Fetch breaks for the selected date
  const { data: dayBreaks = [] } = useQuery({
    queryKey: ['breaks', date],
    queryFn: () => api.getBreaks({ date }),
  });

  const barbersWithBreaks = useMemo(
    () => new Set((dayBreaks as BarberBreak[]).map((brk) => brk.barber_id)),
    [dayBreaks],
  );

  const eligibleBreakBarberIds = useMemo(
    () =>
      new Set(barbers.map((barber: any) => barber.id).filter((id) => !barbersWithBreaks.has(id))),
    [barbers, barbersWithBreaks],
  );

  const canEnterBreakMode = eligibleBreakBarberIds.size > 0;

  useEffect(() => {
    if (interactionMode === 'break' && !canEnterBreakMode) {
      setInteractionMode('booking');
    }
  }, [interactionMode, canEnterBreakMode]);

  const createBreakMutation = useMutation({
    mutationFn: (data: any) => api.createBreak(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
    },
    onError: (err: any) => {
      setFeedbackDialog(getBreakFeedback(err?.message));
    },
  });

  const updateBreakMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateBreak(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
    },
    onError: (err: any) => {
      setFeedbackDialog(getBreakFeedback(err?.message));
    },
  });

  const deleteBreakMutation = useMutation({
    mutationFn: (id: number) => api.deleteBreak(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
    },
    onError: (err: any) => {
      setFeedbackDialog(getBreakFeedback(err?.message));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateBooking(id as any, data),
    onMutate: async ({ id, data }: { id: string; data: any }) => {
      await queryClient.cancelQueries({ queryKey: bookingsQueryKey });
      const previousBookings = queryClient.getQueryData<Booking[]>(bookingsQueryKey) || [];
      queryClient.setQueryData<Booking[]>(bookingsQueryKey, (current = []) =>
        current.map((booking) =>
          booking.id === id
            ? {
                ...booking,
                barber_id: data.barber_id ?? booking.barber_id,
                start_time: data.start_time ?? booking.start_time,
                end_time: data.end_time ?? booking.end_time,
              }
            : booking,
        ),
      );
      return { previousBookings };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousBookings) {
        queryClient.setQueryData(bookingsQueryKey, context.previousBookings);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setNewBooking(null);
    },
  });

  // Time slots based on opening hours
  const timeSlots = useMemo(() => {
    if (!todayHours) return [];
    const slots: string[] = [];
    const startTotal = todayHours.startHour * 60 + todayHours.startMinute;
    const endTotal = todayHours.endHour * 60 + todayHours.endMinute;
    for (let m = startTotal; m < endTotal; m += 15) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
    }
    return slots;
  }, [todayHours]);

  const dragPreview = useMemo(() => {
    if (!dragState.activeId || !dragState.overId || dragState.activeId.startsWith('break-'))
      return null;
    const dashIdx = dragState.overId.indexOf('-');
    if (dashIdx === -1) return null;

    const targetBarberId = Number(dragState.overId.substring(0, dashIdx));
    const time = dragState.overId.substring(dashIdx + 1);
    const booking = bookings.find((item: Booking) => String(item.id) === dragState.activeId);
    if (!booking) return null;

    const duration = new Date(booking.end_time).getTime() - new Date(booking.start_time).getTime();
    const newStart = new Date(brisbaneDateToISO(date, time));
    const newEnd = new Date(newStart.getTime() + duration);
    const overlappingBookingIds = new Set<string>();
    bookings.forEach((item: Booking) => {
      if (item.id === booking.id || item.barber_id !== targetBarberId) return false;
      const itemStart = new Date(item.start_time).getTime();
      const itemEnd = new Date(item.end_time).getTime();
      if (newStart.getTime() < itemEnd && newEnd.getTime() > itemStart) {
        overlappingBookingIds.add(String(item.id));
      }
    });
    const targetOccupied = overlappingBookingIds.size > 0;

    return {
      activeId: dragState.activeId,
      overId: dragState.overId,
      targetBarberId,
      time,
      targetOccupied,
      overlappingBookingIds,
    };
  }, [bookings, date, dragState.activeId, dragState.overId]);

  // Keep a live current-time indicator for the calendar grid.
  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const nowLineTop = useMemo(() => {
    if (!todayHours) return null;
    if (date !== getTodayBrisbane()) return null;

    const [hh, mm] = new Intl.DateTimeFormat('en-AU', {
      timeZone: BRISBANE_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(new Date(nowTick))
      .split(':')
      .map(Number);

    const nowMinutes = hh * 60 + mm;
    const startMinutes = todayHours.startHour * 60 + todayHours.startMinute;
    const endMinutes = todayHours.endHour * 60 + todayHours.endMinute;

    if (Number.isNaN(nowMinutes) || nowMinutes < startMinutes || nowMinutes > endMinutes) {
      return null;
    }

    return ((nowMinutes - startMinutes) / 15) * SLOT_HEIGHT;
  }, [date, nowTick, todayHours]);

  useLayoutEffect(() => {
    if (nowLineTop === null) {
      autoFocusedDateRef.current = '';
      return;
    }
    if (autoFocusedDateRef.current === date) return;
    if (isLoading || barbers.length === 0 || timeSlots.length === 0) return;

    let frame1 = 0;
    let frame2 = 0;

    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        const el = gridScrollRef.current;
        if (!el) return;

        const targetTop = Math.max(nowLineTop - SLOT_HEIGHT * 6, 0);
        el.scrollTop = targetTop;
        autoFocusedDateRef.current = date;
      });
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
    };
  }, [date, nowLineTop, isLoading, barbers.length, timeSlots.length]);

  const handleDragStart = (event: DragStartEvent) => {
    if (readOnly) return;
    setDragState({
      activeId: String(event.active.id),
      overId: null,
    });
  };

  const handleDragOver = (event: DragOverEvent) => {
    setDragState((current) => ({
      activeId: current.activeId || String(event.active.id),
      overId: event.over ? String(event.over.id) : null,
    }));
  };

  const resetDragState = () => {
    setDragState({ activeId: null, overId: null });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      resetDragState();
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);
    const dashIdx = overId.indexOf('-');
    if (dashIdx === -1) {
      resetDragState();
      return;
    }
    const targetBarberId = Number(overId.substring(0, dashIdx));
    const time = overId.substring(dashIdx + 1);

    if (activeId.startsWith('break-')) {
      const breakId = Number(activeId.replace('break-', ''));
      const brk = (dayBreaks as BarberBreak[]).find((b) => b.id === breakId);
      if (!brk) {
        resetDragState();
        return;
      }

      // Breaks stay with the same barber column.
      if (targetBarberId !== brk.barber_id) {
        resetDragState();
        return;
      }

      const duration = new Date(brk.end_time).getTime() - new Date(brk.start_time).getTime();
      const newStart = new Date(brisbaneDateToISO(date, time));
      const newEnd = new Date(newStart.getTime() + duration);

      resetDragState();
      updateBreakMutation.mutate({
        id: breakId,
        data: {
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
        },
      });
      return;
    }

    const bookingId = activeId;

    const booking = bookings.find((b: Booking) => String(b.id) === bookingId);
    if (!booking) {
      resetDragState();
      return;
    }

    // Only allow drag for BOOKED appointments
    if (booking.status !== 'BOOKED') {
      resetDragState();
      return;
    }

    // Only allow drag onto a barber who serves this booking's location.
    // For multi-location barbers (Kiana, Nicola) `barber.location_id`
    // is only their HOME location, so a home-only comparison blocks
    // every drag off/onto them. Use the booking's location_id as the
    // source of truth and check the target's `locations[]` array.
    const targetBarber = barbers.find((b: any) => b.id === targetBarberId);
    if (!targetBarber) {
      resetDragState();
      return;
    }
    const targetServesLocations: number[] = Array.isArray((targetBarber as any).locations)
      ? (targetBarber as any).locations.map((l: any) => Number(l.id))
      : [Number((targetBarber as any).location_id)];
    if (!targetServesLocations.includes(Number(booking.location_id))) {
      resetDragState();
      return;
    }

    const duration = new Date(booking.end_time).getTime() - new Date(booking.start_time).getTime();
    const newStart = new Date(brisbaneDateToISO(date, time));
    const newEnd = new Date(newStart.getTime() + duration);

    // Block drag outside barber's shift range
    const targetShiftRange = barberShiftRanges.get(targetBarberId);
    if (targetShiftRange) {
      const endTime = formatBrisbaneTime(newEnd.toISOString());
      if (time < targetShiftRange.start || endTime > targetShiftRange.end) {
        resetDragState();
        return;
      }
    }

    // Block drag onto breaks
    const overlapsBreak = dayBreaks
      .filter((brk: any) => brk.barber_id === targetBarberId)
      .some((brk: any) => {
        const brkStart = new Date(brk.start_time).getTime();
        const brkEnd = new Date(brk.end_time).getTime();
        return newStart.getTime() < brkEnd && newEnd.getTime() > brkStart;
      });
    if (overlapsBreak) {
      resetDragState();
      return;
    }

    resetDragState();
    updateMutation.mutate({
      id: bookingId,
      data: {
        barber_id: targetBarberId,
        start_time: newStart.toISOString(),
        end_time: newEnd.toISOString(),
      },
    });
  };

  const handleCellClick = (barberId: number, time: string) => {
    if (readOnly) return;
    if (interactionMode === 'break') {
      if (!eligibleBreakBarberIds.has(barberId)) {
        setFeedbackDialog(getBreakFeedback('Only one break per barber is allowed per day'));
        return;
      }

      const existingBreak = (dayBreaks as BarberBreak[]).find((brk) => brk.barber_id === barberId);
      if (existingBreak) {
        setFeedbackDialog(getBreakFeedback('Only one break per barber is allowed per day'));
        return;
      }

      const start = new Date(brisbaneDateToISO(date, time));
      const end = new Date(start.getTime() + 30 * 60 * 1000);
      createBreakMutation.mutate({
        barber_id: barberId,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
      });
      return;
    }

    setNewBooking({ barberId, time });
  };

  const getBookingPosition = (booking: Booking) => {
    if (!todayHours) return { top: 0, height: SLOT_HEIGHT };
    const { hours: startH, minutes: startM } = toBrisbaneHoursMinutes(booking.start_time);
    const { hours: endH, minutes: endM } = toBrisbaneHoursMinutes(booking.end_time);
    const gridStartMin = todayHours.startHour * 60 + todayHours.startMinute;
    const startMinutes = startH * 60 + startM - gridStartMin;
    const endMinutes = endH * 60 + endM - gridStartMin;
    const durationMinutes = endMinutes - startMinutes;
    return {
      top: (startMinutes / 15) * SLOT_HEIGHT,
      height: Math.max((durationMinutes / 15) * SLOT_HEIGHT - 2, SLOT_HEIGHT - 2),
    };
  };

  const getBreakPosition = (brk: any) => {
    if (!todayHours) return { top: 0, height: SLOT_HEIGHT };
    const { hours: startH, minutes: startM } = toBrisbaneHoursMinutes(brk.start_time);
    const { hours: endH, minutes: endM } = toBrisbaneHoursMinutes(brk.end_time);
    const gridStartMin = todayHours.startHour * 60 + todayHours.startMinute;
    const startMinutes = startH * 60 + startM - gridStartMin;
    const endMinutes = endH * 60 + endM - gridStartMin;
    const durationMinutes = endMinutes - startMinutes;
    return {
      top: (startMinutes / 15) * SLOT_HEIGHT,
      height: Math.max((durationMinutes / 15) * SLOT_HEIGHT - 2, SLOT_HEIGHT - 2),
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return { bg: '#d4edda', border: '#28a745', text: '#1e1e1e' };
      case 'CANCELLED':
        return { bg: '#e2e3e5', border: '#6c757d', text: '#555' };
      case 'NO SHOW':
        return { bg: '#f8d7da', border: '#dc3545', text: '#721c24' };
      default:
        return { bg: '#c8ede0', border: '#3b9b78', text: '#1e1e1e' };
    }
  };

  // Client-side search match IDs for highlighting current day bookings
  const searchMatchIds = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return null; // null = no filtering
    const term = searchTerm.toLowerCase();
    const ids = new Set<string>();
    for (const b of bookings) {
      const name = (b.customer_name || '').toLowerCase();
      const email = (b.customer_email || '').toLowerCase();
      const phone = (b.customer_phone || '').toLowerCase();
      const svcs = (b.services || []).map((s: any) => s.name?.toLowerCase() || '').join(' ');
      if (
        name.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        svcs.includes(term)
      ) {
        ids.add(b.id);
      }
    }
    return ids;
  }, [searchTerm, bookings]);

  return (
    <div
      className="page-calendar"
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {/* Header row: title, date nav, location filter, new booking button */}
      <div style={headerStyle}>
        <h1
          style={{
            fontSize: 'var(--font-size-2xl)',
            fontFamily: 'var(--font-family-heading)',
            margin: 0,
          }}
        >
          Calendar
        </h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <button
            onClick={() => setDate((d) => addDays(d, -1))}
            style={btnStyle}
            title="Previous day"
          >
            &lsaquo;
          </button>
          <button onClick={() => setDate(getTodayBrisbane())} style={btnStyle}>
            Today
          </button>
          <span
            style={{
              ...selectStyle,
              minWidth: '160px',
              textAlign: 'center',
              position: 'relative',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => {
              const inp = document.getElementById(
                'calendar-date-picker',
              ) as HTMLInputElement | null;
              inp?.showPicker?.();
              inp?.focus();
            }}
          >
            {formatDateDisplay(date)}
            <input
              id="calendar-date-picker"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                width: '100%',
                height: '100%',
                cursor: 'pointer',
              }}
            />
          </span>
          <button onClick={() => setDate((d) => addDays(d, 1))} style={btnStyle} title="Next day">
            &rsaquo;
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
          {canEnterBreakMode && !readOnly && (
            <button
              onClick={() => {
                setInteractionMode((m) => (m === 'booking' ? 'break' : 'booking'));
              }}
              style={interactionMode === 'break' ? btnAccent : btnStyle}
              title="Toggle break placement mode"
            >
              {interactionMode === 'break' ? 'Break Mode: ON' : 'Add Break'}
            </button>
          )}

          <button
            onClick={() => {
              if (readOnly || barbers.length === 0) return;
              setShowProductSale(true);
            }}
            disabled={readOnly || barbers.length === 0}
            aria-label="Product Sale"
            style={{
              ...btnStyle,
              opacity: readOnly ? 0.5 : 1,
              cursor: readOnly ? 'not-allowed' : 'pointer',
            }}
            title="Create a product-only sale"
          >
            <span className="show-below-tablet" aria-hidden="true">
              🛒
            </span>
            <span className="hide-below-tablet">🛒 Product Sale</span>
          </button>

          {/* Catch Up Time — any shop (Nundah only until it closed; Mitchelton from Nov 2026) */}
          {Boolean(effectiveLocationId) && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => {
                  if (readOnly || barbers.length === 0) return;
                  // If only one barber, skip picker
                  if (barbers.length === 1) {
                    setCatchUpState({ barberId: barbers[0].id, barberName: barbers[0].name });
                  } else {
                    // Toggle dropdown
                    const btn = e.currentTarget;
                    const menu = btn.nextElementSibling as HTMLElement | null;
                    if (menu)
                      menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                  }
                }}
                disabled={readOnly || barbers.length === 0}
                aria-label="Catch Up"
                style={{
                  ...btnStyle,
                  opacity: readOnly ? 0.5 : 1,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                }}
                title="Add catch-up time for a barber"
              >
                <span className="show-below-tablet" aria-hidden="true">
                  ⏱️
                </span>
                <span className="hide-below-tablet">⏱️ Catch Up</span>
              </button>
              {barbers.length > 1 && (
                <div
                  style={{
                    display: 'none',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 100,
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-default)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    minWidth: 160,
                    marginTop: 4,
                  }}
                >
                  {barbers.map((b: any) => (
                    <button
                      key={b.id}
                      onClick={(e) => {
                        setCatchUpState({ barberId: b.id, barberName: b.name });
                        const menu = e.currentTarget.parentElement as HTMLElement;
                        menu.style.display = 'none';
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-text)',
                        fontSize: 'var(--font-size-sm)',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'var(--color-border)')
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Search — fluid width so it doesn't blow out the toolbar on mobile. */}
          <div ref={searchRef} style={{ position: 'relative', flex: '1 1 140px', minWidth: 0 }}>
            <div style={{ position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: 14,
                  color: 'var(--color-text-muted)',
                  pointerEvents: 'none',
                }}
              >
                🔍
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) setSearchOpen(true);
                }}
                placeholder="Search bookings..."
                style={{
                  ...selectStyle,
                  paddingLeft: 32,
                  paddingRight: searchTerm ? 28 : 12,
                  width: '100%',
                  minWidth: 0,
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSearchResults([]);
                    setSearchOpen(false);
                  }}
                  style={{
                    position: 'absolute',
                    right: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 14,
                    color: 'var(--color-text-muted)',
                    padding: 2,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            {/* Search Results Dropdown */}
            {searchOpen && searchResults.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  width: 380,
                  maxHeight: 360,
                  overflowY: 'auto',
                  backgroundColor: '#fff',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 100,
                }}
              >
                <div
                  style={{
                    padding: '8px 12px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    borderBottom: '1px solid var(--color-border)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  {searchLoading && ' · Searching...'}
                </div>
                {searchResults.map((b: any) => {
                  const bDate = b.start_time
                    ? new Intl.DateTimeFormat('en-CA', { timeZone: BRISBANE_TZ }).format(
                        new Date(b.start_time),
                      )
                    : '';
                  const bTime = b.start_time
                    ? new Date(b.start_time).toLocaleTimeString('en-AU', {
                        timeZone: BRISBANE_TZ,
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '';
                  const isCurrentDay = bDate === date;
                  return (
                    <div
                      key={b.id}
                      onClick={() => {
                        if (!isCurrentDay) setDate(bDate);
                        setSearchOpen(false);
                        // Open booking detail after date loads
                        setTimeout(() => setSelectedBookingId(b.id), isCurrentDay ? 0 : 400);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--color-border-light)',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(217,178,86,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 2,
                        }}
                      >
                        <span
                          style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}
                          title={(b as any).barber_requested ? 'Requested this barber' : undefined}
                        >
                          {(b as any).barber_requested ? '⭐ ' : ''}
                          {b.customer_name || (b.source === 'customer' ? 'Guest' : 'Walk-in')}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: 10,
                            backgroundColor:
                              b.status === 'BOOKED'
                                ? 'rgba(59,155,120,0.12)'
                                : b.status === 'COMPLETED'
                                  ? 'rgba(40,167,69,0.12)'
                                  : 'rgba(108,117,125,0.12)',
                            color:
                              b.status === 'BOOKED'
                                ? '#3b9b78'
                                : b.status === 'COMPLETED'
                                  ? '#28a745'
                                  : '#6c757d',
                          }}
                        >
                          {b.status}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {bDate} · {bTime}
                        {b.services?.length > 0 &&
                          ` · ${b.services.map((s: any) => s.name).join(', ')}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {searchOpen &&
              searchTerm.length >= 2 &&
              searchResults.length === 0 &&
              !searchLoading && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    width: 280,
                    padding: '16px 12px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-default)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    zIndex: 100,
                    fontSize: 13,
                    color: 'var(--color-text-muted)',
                  }}
                >
                  No bookings found for "{searchTerm}"
                </div>
              )}
          </div>

          {/* Location filter (Admin only) */}
          {!user?.locationId && (
            <select
              value={locationId || ''}
              onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : undefined)}
              style={selectStyle}
            >
              <option value="">All Locations</option>
              {locations.map((loc: any) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {interactionMode === 'break' && (
        <div
          style={{
            marginBottom: 'var(--spacing-3)',
            padding: '10px 12px',
            borderRadius: 'var(--radius-default)',
            border: '1px dashed #c4a03a',
            backgroundColor: 'rgba(217,178,86,0.08)',
            color: '#7a5400',
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          Break mode is active: click a time cell to add a 30-minute break. Only one break per
          barber/day is allowed, and it must stay at least 1 hour from day start and finish.
        </div>
      )}

      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      ) : isClosed ? (
        <div
          style={{
            padding: 'var(--spacing-12) var(--spacing-6)',
            textAlign: 'center',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--color-surface-elevated)',
          }}
        >
          <p
            style={{
              fontSize: 'var(--font-size-xl)',
              fontFamily: 'var(--font-family-heading)',
              fontWeight: '600',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {isPublicHoliday ? `🏖️ ${publicHolidayName || 'Public Holiday'}` : 'Closed'}
          </p>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {isPublicHoliday
              ? `This day is a public holiday — ${formatDateDisplay(date)}.`
              : effectiveLocationId
                ? `This location is closed on ${formatDateDisplay(date)}.`
                : `All locations are closed on ${formatDateDisplay(date)}.`}
          </p>
        </div>
      ) : barbers.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>
          {effectiveLocationId
            ? 'No barbers found for this location.'
            : 'Select a location to view the calendar.'}
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={resetDragState}
        >
          <div
            ref={gridScrollRef}
            style={{
              display: 'flex',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Time column */}
            <div style={{ flex: '0 0 60px', minWidth: '60px' }}>
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'var(--color-surface-elevated)',
                  borderBottom: '2px solid var(--color-border)',
                  padding: 'var(--spacing-2)',
                  height: '36px',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                }}
              >
                Time
              </div>
              <div style={{ position: 'relative' }}>
                {timeSlots.map((time, idx) => (
                  <div
                    key={`time-${time}`}
                    style={{
                      borderBottom:
                        idx % 4 === 3
                          ? '1px solid var(--color-border)'
                          : '1px solid var(--color-border-light)',
                      padding: '2px 6px',
                      fontSize: '11px',
                      color: 'var(--color-text-muted)',
                      height: `${SLOT_HEIGHT}px`,
                      lineHeight: `${SLOT_HEIGHT}px`,
                      backgroundColor:
                        idx % 4 === 0 ? 'var(--color-surface)' : 'var(--color-background)',
                    }}
                  >
                    {time.endsWith(':00') || idx === 0 ? time : ''}
                  </div>
                ))}
                {nowLineTop !== null && (
                  <>
                    <div
                      style={{
                        position: 'absolute',
                        top: `${nowLineTop}px`,
                        left: '0',
                        right: '0',
                        height: '2px',
                        backgroundColor: '#cf4b3d',
                        zIndex: 12,
                        pointerEvents: 'none',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: `${nowLineTop - 6}px`,
                        left: '-8px',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#c13e30',
                        border: '2px solid #fff',
                        boxShadow: '0 0 0 1px rgba(193,62,48,0.35)',
                        zIndex: 13,
                        pointerEvents: 'none',
                      }}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Barber columns */}
            {barbers.map((barber: any) => (
              <div
                key={`col-${barber.id}`}
                style={{ flex: 1, minWidth: '120px', borderLeft: '1px solid var(--color-border)' }}
              >
                {/* Header */}
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'var(--color-surface-elevated)',
                    borderBottom: '2px solid var(--color-border)',
                    padding: 'var(--spacing-2)',
                    height: '36px',
                    textAlign: 'center',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    fontSize: 'var(--font-size-xs)',
                    letterSpacing: '0.05em',
                  }}
                >
                  {barber.name}
                </div>
                {/* Slots + Bookings container */}
                <div style={{ position: 'relative' }}>
                  {/* Droppable time slot cells */}
                  {timeSlots.map((time, idx) =>
                    (() => {
                      const canAddBreakHere = eligibleBreakBarberIds.has(barber.id);
                      const isBreakPlacementActive = interactionMode === 'break';
                      const isDisabledBreakTarget = isBreakPlacementActive && !canAddBreakHere;
                      const occupiedDropTarget =
                        dragPreview?.overId === `${barber.id}-${time}` &&
                        dragPreview.targetOccupied;

                      // Grey out cells outside the barber's shift range
                      const shiftRange = barberShiftRanges.get(barber.id);
                      const isOutsideShift = shiftRange
                        ? time < shiftRange.start || time >= shiftRange.end
                        : false;

                      return (
                        <DroppableCell
                          key={`${barber.id}-${time}`}
                          id={`${barber.id}-${time}`}
                          onClick={() => !isOutsideShift && handleCellClick(barber.id, time)}
                          disabled={isOutsideShift}
                          occupiedDropTarget={occupiedDropTarget}
                          style={{
                            height: `${SLOT_HEIGHT}px`,
                            borderBottom:
                              idx % 4 === 3
                                ? '1px solid var(--color-border)'
                                : '1px solid var(--color-border-light)',
                            backgroundColor: isOutsideShift
                              ? 'rgba(30,30,30,0.06)'
                              : isDisabledBreakTarget
                                ? 'rgba(30,30,30,0.03)'
                                : idx % 4 === 0
                                  ? 'var(--color-surface)'
                                  : 'var(--color-background)',
                            cursor: isOutsideShift
                              ? 'not-allowed'
                              : isBreakPlacementActive
                                ? canAddBreakHere
                                  ? 'copy'
                                  : 'not-allowed'
                                : 'pointer',
                            opacity: isOutsideShift ? 0.5 : isDisabledBreakTarget ? 0.65 : 1,
                            pointerEvents: isOutsideShift ? 'none' : undefined,
                          }}
                        />
                      );
                    })(),
                  )}
                  {nowLineTop !== null && (
                    <div
                      style={{
                        position: 'absolute',
                        top: `${nowLineTop}px`,
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: '#cf4b3d',
                        zIndex: 8,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  {/* Break blocks (absolute overlay) */}
                  {dayBreaks
                    .filter((brk: any) => brk.barber_id === barber.id)
                    .map((brk: any) => {
                      const { top, height } = getBreakPosition(brk);
                      return (
                        <DraggableBreak
                          key={`break-${brk.id}`}
                          brk={brk}
                          top={top}
                          height={height}
                          onCatchUpClick={(b) => setSelectedCatchUp(b)}
                        />
                      );
                    })}
                  {/* Draggable booking blocks (absolute overlay) */}
                  {(() => {
                    const barberBookings = bookings.filter(
                      (b: Booking) =>
                        b.barber_id === barber.id &&
                        (user?.role === 'ADMIN' || b.status !== 'CANCELLED'),
                    );
                    const overlapLayout = computeOverlapLayout(barberBookings, getBookingPosition);
                    return barberBookings.map((booking: Booking) => {
                      const { top, height } = getBookingPosition(booking);
                      const layout = overlapLayout.get(booking.id);
                      return (
                        <DraggableBooking
                          key={booking.id}
                          booking={booking}
                          top={top}
                          height={height}
                          statusColor={getStatusColor(booking.status)}
                          onClick={() => setSelectedBookingId(booking.id)}
                          colIndex={layout?.index}
                          colTotal={layout?.total}
                          dimmed={searchMatchIds !== null && !searchMatchIds.has(booking.id)}
                          occupiedDrop={dragPreview?.overlappingBookingIds?.has(booking.id)}
                          warning={
                            booking.status === 'BOOKED'
                              ? reliabilityFor(booking.customer_phone)
                              : null
                          }
                        />
                      );
                    });
                  })()}
                </div>
              </div>
            ))}
          </div>
        </DndContext>
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          barberName={
            barbers.find((b: any) => b.id === selectedBooking.barber_id)?.name || 'Unknown'
          }
          allServices={services}
          allBookings={(bookings as Booking[]).filter(
            (b) => b.id !== selectedBooking.id && b.status === 'BOOKED',
          )}
          barbers={barbers}
          allBarbers={allBarbers}
          onClose={() => setSelectedBookingId(null)}
          queryClient={queryClient}
          userRole={user?.role || 'BARBER'}
          readOnly={readOnly}
        />
      )}

      {/* New Booking Modal */}
      {newBooking && (
        <NewBookingModal
          barberId={newBooking.barberId}
          barberName={barbers.find((b: any) => b.id === newBooking.barberId)?.name || ''}
          locationId={
            effectiveLocationId ||
            barbers.find((b: any) => b.id === newBooking.barberId)?.location_id
          }
          date={date}
          time={newBooking.time}
          services={services}
          barbers={barbers}
          isProductOnly={newBooking.isProductOnly || false}
          onClose={() => setNewBooking(null)}
          onSave={(data) => createMutation.mutate(data)}
          saving={createMutation.isPending}
          error={createMutation.error?.message}
        />
      )}

      {/* Product Sale Modal */}
      {showProductSale && (
        <ProductSaleModal
          barbers={barbers}
          locationId={effectiveLocationId || barbers[0]?.location_id}
          date={date}
          onClose={() => setShowProductSale(false)}
          queryClient={queryClient}
        />
      )}

      {/* Catch Up Time Modal — constrained to the BARBER's shift for the day,
          not the location's opening hours. Stops admins assigning catch-ups
          outside when the barber is actually rostered (was happening at
          Nundah where the location opens earlier than some barbers' shifts). */}
      {catchUpState &&
        todayHours &&
        (() => {
          const shift = barberShiftRanges.get(catchUpState.barberId);
          const openTime =
            shift?.start ??
            `${String(todayHours.startHour).padStart(2, '0')}:${String(todayHours.startMinute).padStart(2, '0')}`;
          const closeTime =
            shift?.end ??
            `${String(todayHours.endHour).padStart(2, '0')}:${String(todayHours.endMinute).padStart(2, '0')}`;
          return (
            <CatchUpTimeModal
              barberName={catchUpState.barberName}
              date={date}
              openTime={openTime}
              closeTime={closeTime}
              onClose={() => setCatchUpState(null)}
              onSave={(startTime, minutes) => {
                const start = new Date(brisbaneDateToISO(date, startTime));
                const end = new Date(start.getTime() + minutes * 60 * 1000);
                createBreakMutation.mutate(
                  {
                    barber_id: catchUpState.barberId,
                    start_time: start.toISOString(),
                    end_time: end.toISOString(),
                    type: 'catchup',
                  },
                  {
                    onSuccess: () => setCatchUpState(null),
                    onError: () => setCatchUpState(null),
                  },
                );
              }}
              saving={createBreakMutation.isPending}
            />
          );
        })()}

      {/* Catch Up Detail Popup */}
      {selectedCatchUp && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedCatchUp(null);
          }}
        >
          <div
            style={{
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-default)',
              padding: 'var(--spacing-6)',
              width: 320,
              maxWidth: '90vw',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            <h3
              style={{
                margin: '0 0 var(--spacing-3)',
                fontSize: 'var(--font-size-lg)',
                fontWeight: 600,
              }}
            >
              ⏱️ Catch Up Time
            </h3>
            <p
              style={{
                margin: '0 0 var(--spacing-1)',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {barbers.find((b: any) => b.id === selectedCatchUp.barber_id)?.name || 'Barber'}
            </p>
            <p
              style={{
                margin: '0 0 var(--spacing-4)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {new Date(selectedCatchUp.start_time).toLocaleTimeString('en-AU', {
                timeZone: 'Australia/Brisbane',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}{' '}
              –{' '}
              {new Date(selectedCatchUp.end_time).toLocaleTimeString('en-AU', {
                timeZone: 'Australia/Brisbane',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}{' '}
              (
              {Math.round(
                (new Date(selectedCatchUp.end_time).getTime() -
                  new Date(selectedCatchUp.start_time).getTime()) /
                  60000,
              )}{' '}
              min)
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedCatchUp(null)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'none',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  const id = selectedCatchUp.id;
                  setSelectedCatchUp(null);
                  deleteBreakMutation.mutate(id);
                }}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: '#dc2626',
                  border: '1px solid #dc2626',
                  borderRadius: 'var(--radius-default)',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <FeedbackDialog
        isOpen={!!feedbackDialog}
        title={feedbackDialog?.title || ''}
        message={feedbackDialog?.message || ''}
        tone={feedbackDialog?.tone || 'error'}
        actionLabel={feedbackDialog?.actionLabel || 'Got it'}
        onClose={() => setFeedbackDialog(null)}
      />
    </div>
  );
}

/* ─── Booking Detail Modal ─── */
function BookingDetailModal({
  booking,
  barberName,
  allServices,
  allBookings,
  barbers,
  allBarbers,
  onClose,
  queryClient,
  userRole,
  readOnly,
}: {
  booking: Booking;
  barberName: string;
  allServices: any[];
  allBookings: Booking[];
  barbers: any[];
  allBarbers: any[];
  onClose: () => void;
  queryClient: any;
  userRole: 'ADMIN' | 'BARBER';
  readOnly: boolean;
}) {
  const [editingServices, setEditingServices] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>(
    () => booking.services?.map((s) => (s as any).service_id) || [],
  );
  // Sync selectedServiceIds from live booking data when NOT editing
  useEffect(() => {
    if (editingServices) return;
    const live = booking.services?.map((s) => (s as any).service_id) || [];
    setSelectedServiceIds(live);
  }, [booking.services, editingServices]);
  const [searchFilter, setSearchFilter] = useState('');
  const [showTerminalCheckout, setShowTerminalCheckout] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [pendingRevertId, setPendingRevertId] = useState<string | null>(null);
  // Checkout items from Square catalog — initialized from booking data
  const [checkoutItems, setCheckoutItems] = useState<
    {
      id: string;
      variationId: string;
      name: string;
      variationName: string;
      price: number;
      qty: number;
    }[]
  >(() => (booking as any).checkout_items || []);
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [showAddProducts, setShowAddProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Customer editing state
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [editCustomerName, setEditCustomerName] = useState(booking.customer_name || '');
  const [editCustomerEmail, setEditCustomerEmail] = useState(booking.customer_email || '');
  const [editCustomerPhone, setEditCustomerPhone] = useState(booking.customer_phone || '');
  const [custSuggestions, setCustSuggestions] = useState<any[]>([]);
  const [showCustSuggestions, setShowCustSuggestions] = useState(false);
  const [selectedCustId, setSelectedCustId] = useState<number | null>(null);
  const [activeCustField, setActiveCustField] = useState<'name' | 'email' | 'phone' | null>(null);
  const custNameRef = useRef<HTMLInputElement>(null);
  const custEmailRef = useRef<HTMLInputElement>(null);
  const custPhoneRef = useRef<HTMLInputElement>(null);
  const custSugRef = useRef<HTMLDivElement>(null);

  // Sync customer fields from live booking data when NOT editing
  useEffect(() => {
    if (editingCustomer) return;
    setEditCustomerName(booking.customer_name || '');
    setEditCustomerEmail(booking.customer_email || '');
    setEditCustomerPhone(booking.customer_phone || '');
  }, [booking.customer_name, booking.customer_email, booking.customer_phone, editingCustomer]);

  // Customer notes state
  const [customerNotes, setCustomerNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesInput, setNotesInput] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const customerWarning = useCustomerReliability([booking.customer_phone])(booking.customer_phone);

  // Combined checkout state — for paying multiple bookings in one transaction
  const [showCombine, setShowCombine] = useState(false);
  const [combinedBookingIds, setCombinedBookingIds] = useState<string[]>([]);

  // Loyalty card / Voucher Discount picked at checkout (lib/discounts.ts). Nothing
  // is saved until the barber charges — the backend stores what was applied.
  // Combined checkouts are full price, so selecting a booking to combine
  // drops the discount.
  const [discountChoice, setDiscountChoice] = useState<DiscountChoice | null>(null);
  // Set straight after payment: the paid booking, so the "Book their next
  // cut" step (components/RebookStep.tsx) opens over the panel.
  const [rebookFor, setRebookFor] = useState<Booking | null>(null);
  useEffect(() => {
    if (combinedBookingIds.length > 0) setDiscountChoice(null);
  }, [combinedBookingIds.length]);
  // Editing services replaces the booking_service rows, so a loyalty pick
  // can point at a line that no longer exists — fall back to the first.
  useEffect(() => {
    setDiscountChoice((prev) => {
      if (prev?.type !== 'loyalty') return prev;
      const ids = (booking.services || []).map((s) => Number(s.booking_service_id));
      if (ids.length === 0) return null;
      return ids.includes(Number(prev.bookingServiceId))
        ? prev
        : { type: 'loyalty', bookingServiceId: ids[0] };
    });
  }, [booking.services]);

  // Reschedule dialog state — opens an inline picker (date + start time +
  // barber) and PATCHes the booking on confirm. Duration is preserved by
  // re-summing booking.services so end_time always matches start + duration.
  const [showReschedule, setShowReschedule] = useState(false);

  // Filter: other BOOKED bookings at the same location (already filtered by caller)
  const combinableBookings = allBookings.filter((b) => b.location_id === booking.location_id);

  const combinedTotal = combinedBookingIds.reduce((sum, id) => {
    const b = combinableBookings.find((bk) => bk.id === id);
    if (!b) return sum;
    const svcTotal = (b.services || []).reduce(
      (s: number, svc: any) => s + parseFloat(svc.price || '0'),
      0,
    );
    const itemTotal = ((b as any).checkout_items || []).reduce(
      (s: number, it: any) => s + (Number(it.price) || 0) * Math.max(1, Number(it.qty) || 1),
      0,
    );
    return sum + svcTotal + itemTotal;
  }, 0);

  // Fetch customer notes when booking has email, phone, or name
  useEffect(() => {
    const email = booking.customer_email;
    const phone = booking.customer_phone;
    const name = booking.customer_name;
    if (!email && !phone && !name) {
      setNotesLoaded(true);
      return;
    }
    api
      .getCustomerNotes({
        email: email || undefined,
        phone: phone || undefined,
        name: name || undefined,
      })
      .then((r) => {
        setCustomerNotes(r.notes || '');
        setNotesInput(r.notes || '');
      })
      .catch(() => {})
      .finally(() => setNotesLoaded(true));
  }, [booking.customer_email, booking.customer_phone, booking.customer_name]);

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await api.updateCustomerNotes({
        email: booking.customer_email || undefined,
        phone: booking.customer_phone || undefined,
        name: booking.customer_name || undefined,
        notes: notesInput,
      });
      setCustomerNotes(notesInput);
      setEditingNotes(false);
    } catch {
      /* ignore save error */
    }
    setNotesSaving(false);
  };

  // Customer autocomplete (debounced)
  useEffect(() => {
    if (!editingCustomer || selectedCustId) {
      setCustSuggestions([]);
      setShowCustSuggestions(false);
      return;
    }
    const searchTerm =
      activeCustField === 'email'
        ? editCustomerEmail.trim()
        : activeCustField === 'phone'
          ? editCustomerPhone.trim()
          : editCustomerName.trim();
    if (searchTerm.length < 2) {
      setCustSuggestions([]);
      setShowCustSuggestions(false);
      return;
    }
    const timer = setTimeout(() => {
      api
        .getContacts({ search: searchTerm, limit: 8 })
        .then((res) => {
          setCustSuggestions(res.contacts || []);
          setShowCustSuggestions((res.contacts || []).length > 0);
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [
    editCustomerName,
    editCustomerEmail,
    editCustomerPhone,
    selectedCustId,
    editingCustomer,
    activeCustField,
  ]);

  // Close suggestions on outside click
  useEffect(() => {
    if (!editingCustomer) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        custSugRef.current &&
        !custSugRef.current.contains(target) &&
        !(custNameRef.current && custNameRef.current.contains(target)) &&
        !(custEmailRef.current && custEmailRef.current.contains(target)) &&
        !(custPhoneRef.current && custPhoneRef.current.contains(target))
      ) {
        setShowCustSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editingCustomer]);

  const selectCustSuggestion = (c: any) => {
    setEditCustomerName(c.full_name || '');
    setEditCustomerEmail(c.email || '');
    setEditCustomerPhone(c.mobile || c.phone || '');
    setSelectedCustId(c.id);
    setActiveCustField(null);
    setShowCustSuggestions(false);
  };

  const renderCustDropdown = () => {
    if (!showCustSuggestions || custSuggestions.length === 0) return null;
    return (
      <div
        ref={custSugRef}
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid var(--color-border, #ddd)',
          borderRadius: '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 20,
          maxHeight: '200px',
          overflowY: 'auto',
        }}
      >
        {custSuggestions.map((c: any) => (
          <div
            key={c.id}
            onClick={() => selectCustSuggestion(c)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              fontSize: '13px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(217,178,86,0.08)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ fontWeight: '600' }}>{c.full_name || 'Unknown'}</div>
            <div style={{ fontSize: '11px', opacity: 0.6 }}>
              {[c.email, c.mobile || c.phone].filter(Boolean).join(' \u00b7 ') || 'No contact info'}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const saveCustomerDetails = useCallback(() => {
    api
      .updateBooking(booking.id as any, {
        customer_name: editCustomerName || undefined,
        customer_email: editCustomerEmail || undefined,
        customer_phone: editCustomerPhone || undefined,
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['bookings'] });
        setEditingCustomer(false);
      });
  }, [booking.id, editCustomerName, editCustomerEmail, editCustomerPhone, queryClient]);

  // Sync checkoutItems from live booking data (e.g. after save round-trip)
  useEffect(() => {
    if (showAddProducts) return; // don't overwrite while editing
    const live = (booking as any).checkout_items || [];
    setCheckoutItems(live);
  }, [(booking as any).checkout_items, showAddProducts]);

  // Auto-save checkout items when they change
  const prevCheckoutItemsRef = useRef(checkoutItems);
  useEffect(() => {
    const prev = prevCheckoutItemsRef.current;
    const curr = checkoutItems;
    if (JSON.stringify(prev) === JSON.stringify(curr)) return;
    prevCheckoutItemsRef.current = curr;
    api.updateBooking(booking.id as any, { checkout_items: curr }).then(() => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    });
  }, [checkoutItems]);

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelBooking(id as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      onClose();
    },
  });
  const removeMutation = useMutation({
    mutationFn: (id: string) => api.removeBooking(id as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      onClose();
    },
  });
  const completeMutation = useMutation({
    mutationFn: ({
      id,
      paymentMethod,
      squarePaymentId,
      discount,
    }: {
      id: string;
      paymentMethod: string;
      squarePaymentId?: string;
      /** cash / free only — a card discount is saved when the terminal is rung up. */
      discount?: DiscountChoice | null;
    }) => {
      // If there are combined bookings, complete all of them together
      if (combinedBookingIds.length > 0) {
        const allIds = [id, ...combinedBookingIds];
        return api.combinedComplete(allIds, paymentMethod, squarePaymentId);
      }
      return api.completeBooking(id as any, { paymentMethod, squarePaymentId, discount });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['performance-report'] });
      queryClient.invalidateQueries({ queryKey: ['financial-report'] });
      queryClient.invalidateQueries({ queryKey: ['utilization-report'] });
      queryClient.invalidateQueries({ queryKey: ['comparison-report'] });
      // Offer the next visit before the customer leaves — unless there's
      // nothing to rebook (a product-only sale).
      if ((booking.services || []).length > 0) setRebookFor(booking);
      else onClose();
    },
  });
  const revertCompletionMutation = useMutation({
    mutationFn: (id: string) => api.revertBookingCompletion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['performance-report'] });
      queryClient.invalidateQueries({ queryKey: ['financial-report'] });
      queryClient.invalidateQueries({ queryKey: ['utilization-report'] });
      queryClient.invalidateQueries({ queryKey: ['comparison-report'] });
      onClose();
    },
  });
  const noShowMutation = useMutation({
    mutationFn: (id: string) => api.markNoShow(id as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['performance-report'] });
      onClose();
    },
  });
  const rescheduleMutation = useMutation({
    mutationFn: (args: { startTime: string; endTime: string; barberId: number }) =>
      api.updateBooking(booking.id as any, {
        start_time: args.startTime,
        end_time: args.endTime,
        barber_id: args.barberId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setShowReschedule(false);
      onClose();
    },
  });
  const updateServicesMutation = useMutation({
    mutationFn: (serviceIds: number[]) => {
      const newDuration = serviceIds.reduce((sum, sid) => {
        const svc = allServices.find((s: any) => s.id === sid);
        return sum + (svc?.duration_minutes || 0);
      }, 0);
      const startTime = new Date(booking.start_time);
      const endTime = new Date(startTime.getTime() + newDuration * 60000);
      return api.updateBooking(booking.id as any, {
        service_ids: serviceIds,
        end_time: endTime.toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });

  const statusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string; label: string }> = {
      BOOKED: { bg: 'rgba(217,178,86,0.15)', color: '#D9B256', label: 'Booked' },
      COMPLETED: { bg: 'rgba(40,167,69,0.15)', color: '#28a745', label: 'Completed' },
      CANCELLED: { bg: 'rgba(108,117,125,0.15)', color: '#6c757d', label: 'Cancelled' },
      'NO SHOW': { bg: 'rgba(220,53,69,0.15)', color: '#dc3545', label: 'No Show' },
    };
    const s = map[status] || map.BOOKED;
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '700',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.05em',
          backgroundColor: s.bg,
          color: s.color,
        }}
      >
        {s.label}
      </span>
    );
  };

  const servicesPrice = booking.services?.reduce((sum, s) => sum + (Number(s.price) || 0), 0) || 0;
  const itemsPrice = checkoutItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  const totalPrice = servicesPrice + itemsPrice;
  // Discount: a live preview while BOOKED, what was applied once paid.
  const canDiscount = booking.status === 'BOOKED' && !readOnly && combinedBookingIds.length === 0;
  const discountPreview = previewDiscount(
    booking.services || [],
    checkoutItems,
    canDiscount ? discountChoice : null,
  );
  const amountDue = discountPreview.totalCents / 100;
  const isFreeVisit =
    canDiscount && discountChoice?.type === 'loyalty' && discountPreview.totalCents === 0;
  const savedDiscountCents = Math.round(
    ((booking.services || []).reduce((sum, s) => sum + (Number(s.discount) || 0), 0) +
      (Number(booking.product_discount) || 0)) *
      100,
  );
  const shownDiscountCents =
    booking.status === 'BOOKED' ? discountPreview.discountCents : savedDiscountCents;
  const shownTotal = totalPrice - shownDiscountCents / 100;
  const totalDuration =
    booking.services?.reduce((sum, s) => sum + (s.duration_minutes || s.duration || 0), 0) || 0;
  const actualDuration = Math.round(
    (new Date(booking.end_time).getTime() - new Date(booking.start_time).getTime()) / 60000,
  );

  // Auto-save services when selectedServiceIds changes (only while editing)
  const prevServiceIdsRef = useRef<number[]>(selectedServiceIds);
  useEffect(() => {
    if (!editingServices) return;
    const prev = prevServiceIdsRef.current;
    const curr = selectedServiceIds;
    // Check if actually changed
    if (prev.length === curr.length && prev.every((id, i) => id === curr[i])) return;
    prevServiceIdsRef.current = curr;
    if (curr.length > 0) {
      updateServicesMutation.mutate(curr);
    }
  }, [selectedServiceIds, editingServices]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          width: '100%',
          maxWidth: '540px',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Dark Header ── */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
            padding: '22px 28px',
            position: 'relative',
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: 'rgba(255,255,255,0.6)',
              transition: 'all 0.2s',
            }}
            aria-label="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
            }}
          >
            &times;
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #D9B256, #c4a03a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '700',
                color: '#1e1e1e',
              }}
            >
              {(booking.customer_name || 'W')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ color: '#fff', fontSize: '16px', fontWeight: '600', lineHeight: 1.2 }}>
                {booking.customer_name || (booking.source === 'customer' ? 'Guest' : 'Walk-in')}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '2px' }}>
                with {barberName}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: booking.customer_phone || booking.customer_email ? '10px' : 0,
            }}
          >
            <div
              style={{
                color: '#D9B256',
                fontSize: '20px',
                fontWeight: '700',
                fontFamily: 'var(--font-family-heading)',
              }}
            >
              {formatBrisbaneTime(booking.start_time)} – {formatBrisbaneTime(booking.end_time)}
            </div>
            {statusBadge(booking.status)}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
          {/* Read-only banner for barbers viewing past dates */}
          {readOnly && (
            <div
              style={{
                padding: '8px 12px',
                marginBottom: '16px',
                borderRadius: '8px',
                backgroundColor: 'rgba(108,117,125,0.1)',
                border: '1px solid rgba(108,117,125,0.2)',
                color: '#6c757d',
                fontSize: '12px',
                fontWeight: '500',
                textAlign: 'center',
              }}
            >
              🔒 Past dates are view-only
            </div>
          )}

          {/* Customer Details */}
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.06em',
                  color: 'var(--color-text-muted)',
                }}
              >
                Customer
              </div>
              {booking.status === 'BOOKED' && !readOnly && (
                <button
                  onClick={() => {
                    if (editingCustomer) {
                      saveCustomerDetails();
                    } else {
                      setEditingCustomer(true);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#D9B256',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(217,178,86,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  {editingCustomer ? '✓ Save' : '✎ Edit'}
                </button>
              )}
            </div>

            {editingCustomer ? (
              <div
                style={{
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  backgroundColor: '#fafafa',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <label style={{ ...labelStyle, marginBottom: '4px' }}>Name</label>
                  <input
                    ref={custNameRef}
                    type="text"
                    value={editCustomerName}
                    onChange={(e) => {
                      setEditCustomerName(e.target.value);
                      setSelectedCustId(null);
                      setActiveCustField('name');
                    }}
                    onFocus={() => {
                      setActiveCustField('name');
                      if (custSuggestions.length > 0) setShowCustSuggestions(true);
                    }}
                    placeholder="Search customers..."
                    autoComplete="off"
                    style={{
                      ...inputStyle,
                      backgroundColor: '#fff',
                      fontSize: '13px',
                      padding: '8px 10px',
                    }}
                  />
                  {activeCustField === 'name' && renderCustDropdown()}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <label style={{ ...labelStyle, marginBottom: '4px' }}>Email</label>
                    <input
                      ref={custEmailRef}
                      type="email"
                      value={editCustomerEmail}
                      onChange={(e) => {
                        setEditCustomerEmail(e.target.value);
                        setSelectedCustId(null);
                        setActiveCustField('email');
                      }}
                      onFocus={() => {
                        setActiveCustField('email');
                        if (custSuggestions.length > 0) setShowCustSuggestions(true);
                      }}
                      placeholder="email@example.com"
                      autoComplete="off"
                      style={{
                        ...inputStyle,
                        backgroundColor: '#fff',
                        fontSize: '13px',
                        padding: '8px 10px',
                      }}
                    />
                    {activeCustField === 'email' && renderCustDropdown()}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <label style={{ ...labelStyle, marginBottom: '4px' }}>Mobile</label>
                    <input
                      ref={custPhoneRef}
                      type="tel"
                      value={editCustomerPhone}
                      onChange={(e) => {
                        setEditCustomerPhone(e.target.value);
                        setSelectedCustId(null);
                        setActiveCustField('phone');
                      }}
                      onFocus={() => {
                        setActiveCustField('phone');
                        if (custSuggestions.length > 0) setShowCustSuggestions(true);
                      }}
                      placeholder="04XX XXX XXX"
                      autoComplete="off"
                      style={{
                        ...inputStyle,
                        backgroundColor: '#fff',
                        fontSize: '13px',
                        padding: '8px 10px',
                      }}
                    />
                    {activeCustField === 'phone' && renderCustDropdown()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setEditingCustomer(false);
                      setEditCustomerName(booking.customer_name || '');
                      setEditCustomerEmail(booking.customer_email || '');
                      setEditCustomerPhone(booking.customer_phone || '');
                    }}
                    style={{
                      background: 'none',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCustomerDetails}
                    style={{
                      background: '#D9B256',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#1e1e1e',
                      cursor: 'pointer',
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    fontSize: '13px',
                    backgroundColor: '#fff',
                  }}
                >
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>Name</span>
                  <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>
                    {booking.customer_name || (booking.source === 'customer' ? 'Guest' : 'Walk-in')}
                  </span>
                </div>
                {booking.customer_email && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      fontSize: '13px',
                      backgroundColor: '#fafafa',
                      borderTop: '1px solid var(--color-border-light)',
                    }}
                  >
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                      Email
                    </span>
                    <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>
                      {booking.customer_email}
                    </span>
                  </div>
                )}
                {booking.customer_phone && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      fontSize: '13px',
                      backgroundColor: booking.customer_email ? '#fff' : '#fafafa',
                      borderTop: '1px solid var(--color-border-light)',
                    }}
                  >
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                      Mobile
                    </span>
                    <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>
                      {booking.customer_phone}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {customerWarning && (
            <div
              role="status"
              style={{
                marginBottom: '16px',
                padding: '10px 14px',
                fontSize: '13px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255,193,7,0.15)',
                border: '1px solid rgba(255,193,7,0.6)',
              }}
            >
              ⚠ {reliabilityText(customerWarning)}
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                {customerWarning.flag === 'keeps-moving'
                  ? 'Keeps moving bookings without coming in — confirm before the day.'
                  : 'Worth confirming with them before the day.'}{' '}
                Last 12 months.
              </div>
            </div>
          )}

          {/* Customer Notes */}
          {notesLoaded &&
            (booking.customer_email || booking.customer_phone || booking.customer_name) && (
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    Notes
                  </div>
                  {!editingNotes && (
                    <button
                      onClick={() => {
                        setNotesInput(customerNotes);
                        setEditingNotes(true);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '12px',
                        color: 'var(--color-accent)',
                        cursor: 'pointer',
                        padding: '2px 6px',
                      }}
                    >
                      {customerNotes ? 'Edit' : '+ Add'}
                    </button>
                  )}
                </div>
                {editingNotes ? (
                  <div>
                    <textarea
                      value={notesInput}
                      onChange={(e) => setNotesInput(e.target.value)}
                      placeholder="Add notes about this customer..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: '13px',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                        color: '#1a1a2e',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        marginTop: '6px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <button
                        onClick={() => setEditingNotes(false)}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0',
                          background: 'transparent',
                          color: '#888',
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveNotes}
                        disabled={notesSaving}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          border: 'none',
                          background: 'var(--color-accent)',
                          color: '#000',
                          cursor: notesSaving ? 'wait' : 'pointer',
                          fontWeight: '600',
                          opacity: notesSaving ? 0.7 : 1,
                        }}
                      >
                        {notesSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : customerNotes ? (
                  <div
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      color: '#333',
                      backgroundColor: '#fafafa',
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.4',
                    }}
                  >
                    {customerNotes}
                  </div>
                ) : null}
              </div>
            )}

          {/* Services */}
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.06em',
                  color: 'var(--color-text-muted)',
                }}
              >
                Services
              </div>
              {booking.status === 'BOOKED' && !readOnly && (
                <button
                  onClick={() => {
                    if (editingServices) {
                      setEditingServices(false);
                      setSearchFilter('');
                    } else {
                      prevServiceIdsRef.current = selectedServiceIds;
                      setEditingServices(true);
                      setSearchFilter('');
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#D9B256',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(217,178,86,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  {editingServices ? 'Done' : '+ Add Service'}
                </button>
              )}
            </div>

            {/* Service list — always visible, with inline remove for BOOKED */}
            {booking.services && booking.services.length > 0 && (
              <div
                style={{
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {booking.services.map((s: any, i: number) => (
                  <div
                    key={`svc-${i}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      fontSize: '13px',
                      backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa',
                      borderTop: i > 0 ? '1px solid var(--color-border-light)' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {booking.status === 'BOOKED' && (
                        <button
                          onClick={() => {
                            if (!editingServices) {
                              prevServiceIdsRef.current = selectedServiceIds;
                              setEditingServices(true);
                              setSearchFilter('');
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            lineHeight: 1,
                            color: '#ccc',
                            padding: '2px',
                            transition: 'color 0.15s',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#D9B256';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#ccc';
                          }}
                          title="Edit services"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                      {booking.status === 'BOOKED' && selectedServiceIds.length > 1 && (
                        <button
                          onClick={() => {
                            setEditingServices(true);
                            setSelectedServiceIds((prev) => {
                              const next = [...prev];
                              next.splice(i, 1);
                              return next;
                            });
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            lineHeight: 1,
                            color: '#ccc',
                            padding: '2px',
                            transition: 'color 0.15s',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#dc3545';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#ccc';
                          }}
                          title="Remove service"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      )}
                      <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>
                        {s.name}
                      </span>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                        {s.duration_minutes || s.duration || 0} min
                      </span>
                    </div>
                    <span style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                      ${Number(s.price).toFixed(2)}
                    </span>
                  </div>
                ))}
                {/* Auto-save indicator */}
                {updateServicesMutation.isPending && (
                  <div
                    style={{
                      textAlign: 'center',
                      fontSize: '11px',
                      color: 'var(--color-text-muted)',
                      padding: '6px',
                    }}
                  >
                    Saving...
                  </div>
                )}
              </div>
            )}
            {(!booking.services || booking.services.length === 0) && (
              <div
                style={{
                  padding: '12px 14px',
                  fontSize: '13px',
                  color: 'var(--color-text-muted)',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                {booking.is_product_only
                  ? '🛒 Product sale — no services'
                  : `No services assigned · ${actualDuration} min booking`}
              </div>
            )}

            {/* Add Service picker — after service list */}
            {editingServices && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '7px 10px',
                    fontSize: '13px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    marginBottom: '6px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <div
                  style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                  }}
                >
                  {allServices
                    .filter((s: any) => s.name.toLowerCase().includes(searchFilter.toLowerCase()))
                    .map((svc: any) => (
                      <div
                        key={svc.id}
                        onClick={() => setSelectedServiceIds((prev) => [...prev, svc.id])}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          border: '1px solid var(--color-border-light)',
                          fontSize: '13px',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--color-surface-elevated)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <span>{svc.name}</span>
                        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                          {svc.duration_minutes}min · ${Number(svc.price).toFixed(0)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Products at checkout — only for BOOKED */}
          {booking.status === 'BOOKED' && !editingServices && (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.06em',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Products
                </div>
                <button
                  onClick={() => {
                    if (showAddProducts) {
                      setShowAddProducts(false);
                      setProductSearch('');
                    } else {
                      setShowAddProducts(true);
                      setProductSearch('');
                      if (catalogItems.length === 0 && !catalogLoading) {
                        setCatalogLoading(true);
                        api
                          .getCatalogItems()
                          .then((res: any) => setCatalogItems(res.items || []))
                          .catch(() => setCatalogItems([]))
                          .finally(() => setCatalogLoading(false));
                      }
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#D9B256',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(217,178,86,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  {showAddProducts ? 'Done' : '+ Add Product'}
                </button>
              </div>

              {/* Product list with inline remove */}
              {checkoutItems.length > 0 && (
                <div
                  style={{
                    border: '1px solid var(--color-border-light)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  {checkoutItems.map((item, idx) => (
                    <div
                      key={`item-${idx}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 14px',
                        fontSize: '13px',
                        backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                        borderTop: idx > 0 ? '1px solid var(--color-border-light)' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                          onClick={() => {
                            setCheckoutItems((prev) => {
                              const updated = [...prev];
                              if (updated[idx].qty > 1) {
                                updated[idx] = { ...updated[idx], qty: updated[idx].qty - 1 };
                              } else {
                                updated.splice(idx, 1);
                              }
                              return updated;
                            });
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            lineHeight: 1,
                            color: '#ccc',
                            padding: '2px',
                            transition: 'color 0.15s',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#dc3545';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#ccc';
                          }}
                          title="Remove product"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                        <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>
                          {item.name}
                          {item.variationName !== 'Regular' && item.variationName !== item.name
                            ? ` (${item.variationName})`
                            : ''}
                        </span>
                        {item.qty > 1 && (
                          <span style={{ color: '#D9B256', fontWeight: '600', fontSize: '12px' }}>
                            x{item.qty}
                          </span>
                        )}
                      </div>
                      <span style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                        ${(item.price * item.qty).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Product picker — after product list */}
              {showAddProducts && (
                <div style={{ marginTop: '8px' }}>
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      fontSize: '13px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div
                    style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px',
                    }}
                  >
                    {catalogLoading ? (
                      <div
                        style={{
                          padding: '12px',
                          fontSize: '12px',
                          color: 'var(--color-text-muted)',
                          textAlign: 'center',
                        }}
                      >
                        Loading products...
                      </div>
                    ) : catalogItems.length === 0 ? (
                      <div
                        style={{
                          padding: '12px',
                          fontSize: '12px',
                          color: 'var(--color-text-muted)',
                          textAlign: 'center',
                        }}
                      >
                        No products in Square catalog.
                      </div>
                    ) : (
                      (() => {
                        const filtered = catalogItems.filter((item: any) =>
                          item.name.toLowerCase().includes(productSearch.toLowerCase()),
                        );
                        if (filtered.length === 0) {
                          return (
                            <div
                              style={{
                                padding: '12px',
                                fontSize: '12px',
                                color: 'var(--color-text-muted)',
                                textAlign: 'center',
                              }}
                            >
                              No products match "{productSearch}"
                            </div>
                          );
                        }
                        return filtered.map((item: any) =>
                          item.variations.map((v: any) => (
                            <div
                              key={v.id}
                              onClick={() => {
                                setCheckoutItems((prev) => {
                                  const existing = prev.findIndex((ci) => ci.variationId === v.id);
                                  if (existing >= 0) {
                                    const updated = [...prev];
                                    updated[existing] = {
                                      ...updated[existing],
                                      qty: updated[existing].qty + 1,
                                    };
                                    return updated;
                                  }
                                  return [
                                    ...prev,
                                    {
                                      id: item.id,
                                      variationId: v.id,
                                      name: item.name,
                                      variationName: v.name,
                                      price: v.priceCents / 100,
                                      qty: 1,
                                    },
                                  ];
                                });
                              }}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                border: '1px solid var(--color-border-light)',
                                fontSize: '13px',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--color-surface-elevated)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span>
                                {item.name}
                                {v.name !== 'Regular' && v.name !== item.name && (
                                  <span
                                    style={{
                                      color: 'var(--color-text-muted)',
                                      marginLeft: 4,
                                      fontSize: '12px',
                                    }}
                                  >
                                    ({v.name})
                                  </span>
                                )}
                              </span>
                              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                                ${(v.priceCents / 100).toFixed(2)}
                              </span>
                            </div>
                          )),
                        );
                      })()
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Products summary — for completed/non-BOOKED bookings with checkout items */}
          {booking.status !== 'BOOKED' && checkoutItems.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.06em',
                  color: 'var(--color-text-muted)',
                  marginBottom: '8px',
                }}
              >
                Products
              </div>
              <div
                style={{
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {checkoutItems.map((item, idx) => (
                  <div
                    key={`completed-item-${idx}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 14px',
                      fontSize: '13px',
                      borderBottom:
                        idx < checkoutItems.length - 1
                          ? '1px solid var(--color-border-light)'
                          : 'none',
                    }}
                  >
                    <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>
                      {item.name}
                      {item.qty > 1 && (
                        <span
                          style={{
                            color: 'var(--color-text-muted)',
                            fontSize: '12px',
                            marginLeft: '4px',
                          }}
                        >
                          x{item.qty}
                        </span>
                      )}
                    </span>
                    <span style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                      ${(item.price * item.qty).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Discount — loyalty card / Voucher Discount (lib/discounts.ts) */}
          {canDiscount && !showTerminalCheckout && (
            <CheckoutDiscount
              services={booking.services || []}
              choice={discountChoice}
              discountCents={discountPreview.discountCents}
              onChange={setDiscountChoice}
            />
          )}
          {booking.status !== 'BOOKED' && booking.discount_type && savedDiscountCents > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 14px',
                marginBottom: '8px',
                fontSize: '13px',
                borderRadius: '8px',
                backgroundColor: 'rgba(40,167,69,0.08)',
                color: '#1e7e34',
                fontWeight: 600,
              }}
            >
              <span>{DISCOUNT_LABELS[booking.discount_type]}</span>
              <span>−${(savedDiscountCents / 100).toFixed(2)}</span>
            </div>
          )}

          {/* Total row — after services and products */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 14px',
              fontSize: '13px',
              backgroundColor: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-border-light)',
              borderRadius: '8px',
              marginBottom: '16px',
            }}
          >
            <span style={{ fontWeight: '600' }}>
              Total · {actualDuration} min
              {totalDuration !== actualDuration && (
                <span
                  style={{
                    color: 'var(--color-text-muted)',
                    fontWeight: '400',
                    fontSize: '11px',
                    marginLeft: '4px',
                  }}
                >
                  (services: {totalDuration} min)
                </span>
              )}
            </span>
            <span style={{ fontWeight: '700', color: '#D9B256', fontSize: '15px' }}>
              {shownDiscountCents > 0 && (
                <span
                  style={{
                    textDecoration: 'line-through',
                    color: 'var(--color-text-muted)',
                    fontWeight: '400',
                    fontSize: '12px',
                    marginRight: '6px',
                  }}
                >
                  ${totalPrice.toFixed(2)}
                </span>
              )}
              ${shownTotal.toFixed(2)}
            </span>
          </div>

          {/* Combined bookings details — shown when bookings are selected */}
          {combinedBookingIds.length > 0 && (
            <div
              style={{
                backgroundColor: 'rgba(217,178,86,0.06)',
                border: '1px solid rgba(217,178,86,0.25)',
                borderRadius: '8px',
                padding: '12px 14px',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#D9B256',
                  marginBottom: '10px',
                }}
              >
                🔗 Combined Bookings
              </div>
              {combinedBookingIds.map((cId) => {
                const cb = combinableBookings.find((b) => b.id === cId);
                if (!cb) return null;
                const cbBarber =
                  barbers.find((br: any) => br.id === cb.barber_id)?.name || 'Unknown';
                const cbServices = cb.services || [];
                const cbTotal = cbServices.reduce(
                  (s: number, svc: any) => s + parseFloat(svc.price || '0'),
                  0,
                );
                return (
                  <div
                    key={cId}
                    style={{
                      padding: '8px 0',
                      borderBottom: '1px solid rgba(217,178,86,0.15)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}
                    >
                      <span style={{ fontSize: '13px', fontWeight: '600' }}>
                        {cb.customer_name || 'Guest'}{' '}
                        <span style={{ color: 'var(--color-text-muted)', fontWeight: '400' }}>
                          with {cbBarber}
                        </span>
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#D9B256' }}>
                        ${cbTotal.toFixed(2)}
                      </span>
                    </div>
                    {cbServices.map((svc: any, i: number) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          color: 'var(--color-text-muted)',
                          padding: '1px 0',
                        }}
                      >
                        <span>
                          {svc.name} · {svc.duration} min
                        </span>
                        <span>${parseFloat(svc.price || '0').toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '10px',
                  fontSize: '14px',
                  fontWeight: '700',
                }}
              >
                <span>Combined Total</span>
                <span style={{ color: '#D9B256', fontSize: '16px' }}>
                  ${(totalPrice + combinedTotal).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Action buttons — only for BOOKED */}
          {booking.status === 'BOOKED' && !readOnly && (
            <>
              {/* Combine Bookings toggle */}
              {combinableBookings.length > 0 && !showTerminalCheckout && (
                <div style={{ marginBottom: '10px' }}>
                  <button
                    onClick={() => {
                      setShowCombine(!showCombine);
                      if (showCombine) setCombinedBookingIds([]);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: showCombine ? '#D9B256' : 'var(--color-text-muted)',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '4px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    🔗 {showCombine ? 'Cancel combine' : 'Combine with other booking'}
                  </button>
                  {showCombine && (
                    <div
                      style={{
                        marginTop: '8px',
                        padding: '10px',
                        backgroundColor: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--color-text-muted)',
                          marginBottom: '8px',
                        }}
                      >
                        Select bookings to pay together:
                      </div>
                      {combinableBookings.map((b) => {
                        const bBarber =
                          barbers.find((br: any) => br.id === b.barber_id)?.name || 'Unknown';
                        const bServices = (b.services || []).map((s: any) => s.name).join(', ');
                        const bTotal = (b.services || []).reduce(
                          (s: number, svc: any) => s + parseFloat(svc.price || '0'),
                          0,
                        );
                        const isSelected = combinedBookingIds.includes(b.id);
                        return (
                          <label
                            key={b.id}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              padding: '8px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? 'rgba(217,178,86,0.12)' : 'transparent',
                              border: isSelected
                                ? '1px solid rgba(217,178,86,0.4)'
                                : '1px solid transparent',
                              marginBottom: '4px',
                              transition: 'all 0.15s',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setCombinedBookingIds((prev) =>
                                  prev.includes(b.id)
                                    ? prev.filter((id) => id !== b.id)
                                    : [...prev, b.id],
                                );
                              }}
                              style={{ marginTop: '2px', accentColor: '#D9B256' }}
                            />
                            <div style={{ flex: 1, fontSize: '12px' }}>
                              <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                                {b.customer_name || 'Guest'} — {bBarber}
                              </div>
                              <div style={{ color: 'var(--color-text-muted)', marginTop: '2px' }}>
                                {bServices || 'No services'} · ${bTotal.toFixed(2)}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                      {combinedBookingIds.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 0 0',
                            borderTop: '1px solid var(--color-border)',
                            marginTop: '8px',
                            fontSize: '13px',
                            fontWeight: '700',
                          }}
                        >
                          <span>Combined Total</span>
                          <span style={{ color: '#D9B256' }}>
                            ${(totalPrice + combinedTotal).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {booking.status === 'BOOKED' &&
            !readOnly &&
            (showTerminalCheckout ? (
              <TerminalCheckoutFlow
                booking={booking}
                totalPrice={combinedBookingIds.length > 0 ? totalPrice + combinedTotal : amountDue}
                checkoutItems={checkoutItems}
                combinedBookingIds={combinedBookingIds}
                discount={canDiscount ? discountChoice : null}
                onComplete={(paymentId) => {
                  completeMutation.mutate({
                    id: String(booking.id),
                    paymentMethod: 'card',
                    squarePaymentId: paymentId,
                  });
                }}
                onBack={() => setShowTerminalCheckout(false)}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Checkout row — a loyalty card that covers everything has
                    nothing to charge, so it completes without the terminal. */}
                {isFreeVisit ? (
                  <button
                    onClick={() =>
                      completeMutation.mutate({
                        id: String(booking.id),
                        paymentMethod: 'free',
                        discount: discountChoice,
                      })
                    }
                    disabled={completeMutation.isPending}
                    style={{
                      minHeight: '44px',
                      padding: '10px 0',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                      color: '#D9B256',
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    🎟 Complete — free with loyalty card
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setShowTerminalCheckout(true)}
                      style={{
                        flex: 1,
                        padding: '10px 0',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                        color: '#D9B256',
                        fontWeight: '600',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.85';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      💳 Card
                    </button>
                    <button
                      onClick={() =>
                        completeMutation.mutate({
                          id: String(booking.id),
                          paymentMethod: 'cash',
                          discount: canDiscount ? discountChoice : null,
                        })
                      }
                      style={{
                        flex: 1,
                        padding: '10px 0',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                        color: '#D9B256',
                        fontWeight: '600',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.85';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      💵 Cash
                    </button>
                  </div>
                )}
                {/* Secondary actions */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowReschedule(true)}
                    disabled={readOnly}
                    style={{
                      flex: '1 1 100%',
                      padding: '9px 0',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: '#fff',
                      color: 'var(--color-text-muted)',
                      fontWeight: '500',
                      fontSize: '13px',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      opacity: readOnly ? 0.5 : 1,
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!readOnly) {
                        e.currentTarget.style.borderColor = 'var(--color-accent)';
                        e.currentTarget.style.color = 'var(--color-accent)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                    }}
                  >
                    🗓 Reschedule
                  </button>
                  <button
                    onClick={() => noShowMutation.mutate(String(booking.id))}
                    disabled={readOnly}
                    style={{
                      flex: 1,
                      padding: '9px 0',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: '#fff',
                      color: 'var(--color-text-muted)',
                      fontWeight: '500',
                      fontSize: '13px',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      opacity: readOnly ? 0.5 : 1,
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!readOnly) {
                        e.currentTarget.style.borderColor = '#6c757d';
                        e.currentTarget.style.color = '#6c757d';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                    }}
                  >
                    No Show
                  </button>
                  <button
                    onClick={() => cancelMutation.mutate(String(booking.id))}
                    disabled={readOnly}
                    style={{
                      flex: 1,
                      padding: '9px 0',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: '#fff',
                      color: 'var(--color-text-muted)',
                      fontWeight: '500',
                      fontSize: '13px',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      opacity: readOnly ? 0.5 : 1,
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!readOnly) {
                        e.currentTarget.style.borderColor = 'var(--color-error)';
                        e.currentTarget.style.color = 'var(--color-error)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                    }}
                  >
                    Cancel Booking
                  </button>
                </div>
                {/* Admin-only: Remove booking (hides from UI permanently) */}
                {userRole === 'ADMIN' && (
                  <button
                    onClick={() => setPendingRemoveId(String(booking.id))}
                    style={{
                      width: '100%',
                      padding: '9px 0',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: '#fff',
                      color: 'var(--color-text-muted)',
                      fontWeight: '500',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#dc3545';
                      e.currentTarget.style.color = '#dc3545';
                      e.currentTarget.style.backgroundColor = 'rgba(220,53,69,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                      e.currentTarget.style.backgroundColor = '#fff';
                    }}
                  >
                    🗑 Remove Appointment
                  </button>
                )}
              </div>
            ))}

          {/* Admin-only: Revert a completed booking back to BOOKED. Undoes
              status/payment/inventory + the customer.appointment_count bump.
              Card payments get an extra warning in the confirm dialog —
              the revert here does NOT issue a Square refund. */}
          {booking.status === 'COMPLETED' && userRole === 'ADMIN' && !readOnly && (
            <div style={{ marginTop: '12px' }}>
              <button
                onClick={() => setPendingRevertId(String(booking.id))}
                disabled={revertCompletionMutation.isPending}
                style={{
                  width: '100%',
                  padding: '9px 0',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: '#fff',
                  color: 'var(--color-text-muted)',
                  fontWeight: '500',
                  fontSize: '13px',
                  cursor: revertCompletionMutation.isPending ? 'wait' : 'pointer',
                  opacity: revertCompletionMutation.isPending ? 0.6 : 1,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!revertCompletionMutation.isPending) {
                    e.currentTarget.style.borderColor = '#d9a843';
                    e.currentTarget.style.color = '#b8862a';
                    e.currentTarget.style.backgroundColor = 'rgba(217,168,67,0.06)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                ↩ Revert Completion
              </button>
            </div>
          )}

          {/* Admin-only: Remove button for non-BOOKED statuses */}
          {booking.status !== 'BOOKED' && userRole === 'ADMIN' && (
            <div style={{ marginTop: '12px' }}>
              <button
                onClick={() => setPendingRemoveId(String(booking.id))}
                style={{
                  width: '100%',
                  padding: '9px 0',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: '#fff',
                  color: 'var(--color-text-muted)',
                  fontWeight: '500',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#dc3545';
                  e.currentTarget.style.color = '#dc3545';
                  e.currentTarget.style.backgroundColor = 'rgba(220,53,69,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                🗑 Remove Appointment
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!pendingRemoveId}
        title="Remove Appointment"
        message="This appointment will be removed from the calendar and cancelled in Square. This action cannot be undone."
        confirmLabel="Remove"
        tone="danger"
        onConfirm={() => {
          if (pendingRemoveId) removeMutation.mutate(pendingRemoveId);
          setPendingRemoveId(null);
        }}
        onCancel={() => setPendingRemoveId(null)}
      />

      <ConfirmDialog
        isOpen={!!pendingRevertId}
        title="Revert this completion?"
        message={
          // The backend treats anything other than 'cash' as a Square
          // payment for warning purposes — DB values are 'cash' / 'card' /
          // 'square'. Mirror that here so the admin sees the refund
          // warning whenever real money may have moved through Square.
          // 'free' is a loyalty visit — nothing was charged.
          (booking as any).payment_method &&
          !['cash', 'free'].includes((booking as any).payment_method.toLowerCase?.())
            ? 'This will set the booking back to BOOKED and clear the payment, inventory, and stats stamps. ⚠ This booking was paid via Square (card) — reverting here does NOT refund the customer. Issue any refund in Square first.'
            : 'This will set the booking back to BOOKED and clear the cash payment, restore any product stock, and undo the customer’s appointment-count bump.'
        }
        confirmLabel="Revert"
        tone="warning"
        onConfirm={() => {
          if (pendingRevertId) revertCompletionMutation.mutate(pendingRevertId);
          setPendingRevertId(null);
        }}
        onCancel={() => setPendingRevertId(null)}
      />

      {rebookFor && (
        <RebookStep
          booking={rebookFor as any}
          barbers={allBarbers
            .filter((b: any) => b.location_id === rebookFor.location_id && b.active !== false)
            .map((b: any) => ({ id: Number(b.id), name: b.name }))}
          onDone={() => {
            setRebookFor(null);
            onClose();
          }}
        />
      )}

      {showReschedule && (
        <RescheduleDialog
          booking={booking}
          barbers={allBarbers}
          allBookings={allBookings}
          onCancel={() => setShowReschedule(false)}
          onConfirm={(startTime, endTime, barberId) =>
            rescheduleMutation.mutate({ startTime, endTime, barberId })
          }
          submitting={rescheduleMutation.isPending}
        />
      )}
    </div>
  );
}

/* ─── Reschedule Dialog ───
 *
 * Inline modal opened from BookingDetailModal. The user picks a new
 * date, start time, and (optionally) barber. Duration is derived from
 * the booking's existing services so end_time always stays consistent
 * with start + total duration — admins don't need to compute end-time
 * themselves.
 *
 * Conflict check: warns (but doesn't block) if the new slot overlaps
 * another BOOKED appointment for the chosen barber. The backend would
 * accept the overlap silently otherwise; the warning gives the admin a
 * chance to bail before double-booking. */
function RescheduleDialog({
  booking,
  barbers,
  allBookings,
  onCancel,
  onConfirm,
  submitting,
}: {
  booking: Booking;
  barbers: any[];
  allBookings: Booking[];
  onCancel: () => void;
  onConfirm: (startTime: string, endTime: string, barberId: number) => void;
  submitting: boolean;
}) {
  const totalDurationMin = (booking.services || []).reduce(
    (sum: number, s: any) => sum + (Number(s.duration) || 0),
    0,
  );
  // Default date/time picker values from the existing booking, local-tz.
  const currentStart = new Date(booking.start_time);
  const pad = (n: number) => String(n).padStart(2, '0');
  const defaultDate = `${currentStart.getFullYear()}-${pad(currentStart.getMonth() + 1)}-${pad(currentStart.getDate())}`;
  const defaultTime = `${pad(currentStart.getHours())}:${pad(currentStart.getMinutes())}`;

  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState(defaultTime);
  const [barberId, setBarberId] = useState<number>(booking.barber_id as number);

  // Filter barbers to same location (avoids cross-location reassign by accident).
  const sameLocationBarbers = barbers.filter((b: any) => b.location_id === booking.location_id);

  // Opening hours for the booking's location — used to block reschedules
  // that fall outside business hours (e.g. accidentally typing 00:30 in
  // the time picker).
  const { data: openingHours } = useQuery({
    queryKey: ['opening-hours', booking.location_id],
    queryFn: () => api.getOpeningHours(booking.location_id as number),
  });

  // Rosters for the proposed date — used to (a) filter the barber
  // dropdown to staff actually working that day and (b) block reschedules
  // where the slot falls outside the chosen barber's shift hours.
  const { data: dayShifts, isFetching: shiftsLoading } = useQuery<any[]>({
    queryKey: ['rosters', date, booking.location_id],
    queryFn: () =>
      api.getRosters({
        startDate: `${date}T00:00:00`,
        endDate: `${date}T23:59:59`,
        locationId: String(booking.location_id),
      }),
    enabled: !!booking.location_id && !isNaN(new Date(`${date}T00:00:00`).getTime()),
  });
  const shifts = dayShifts ?? [];
  const shiftsLoaded = !shiftsLoading && dayShifts !== undefined;

  // Set of barber IDs rostered on the chosen day.
  const rosteredBarberIds = useMemo(() => {
    const ids = new Set<number>();
    for (const s of shifts) ids.add(Number(s.barber_id));
    return ids;
  }, [shifts]);

  // Strictly only show barbers who have a shift on this day — matches the
  // main Calendar's behaviour. While fetching the roster we hide everyone
  // to avoid showing stale rows from the previous date selection.
  const eligibleBarbers = useMemo(() => {
    if (!shiftsLoaded) return [];
    return sameLocationBarbers.filter((b: any) => rosteredBarberIds.has(Number(b.id)));
  }, [sameLocationBarbers, shiftsLoaded, rosteredBarberIds]);

  // If the chosen date no longer has the currently-selected barber on
  // shift (e.g. user moved to a day the original barber doesn't work),
  // fall back to the first rostered barber so the select doesn't show a
  // stale ID that no option matches.
  useEffect(() => {
    if (!shiftsLoaded) return;
    if (eligibleBarbers.length === 0) return;
    if (!eligibleBarbers.some((b: any) => Number(b.id) === Number(barberId))) {
      setBarberId(Number(eligibleBarbers[0].id));
    }
  }, [shiftsLoaded, eligibleBarbers, barberId]);

  // Build the proposed start/end Date objects from current picker state.
  const newStart = new Date(`${date}T${time}`);
  const newEnd = new Date(newStart.getTime() + totalDurationMin * 60_000);
  const invalidDate = isNaN(newStart.getTime());

  // Opening-hours validation. Each row: { day_of_week (0=Sun..6=Sat),
  // open_time, close_time } as HH:MM:SS strings. Closed days have no row.
  type OpeningHoursIssue =
    | { kind: 'closed'; dayLabel: string }
    | { kind: 'outside'; openLabel: string; closeLabel: string };
  const openingIssue: OpeningHoursIssue | null = (() => {
    if (invalidDate || !openingHours) return null;
    const dow = newStart.getDay();
    const row = openingHours.find((h: any) => h.day_of_week === dow);
    const dayLabels = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    if (!row) return { kind: 'closed', dayLabel: dayLabels[dow] };
    const norm = (t: string) => (t.split(':').length === 2 ? `${t}:00` : t);
    const openMs = new Date(`${date}T${norm(row.open_time)}`).getTime();
    const closeMs = new Date(`${date}T${norm(row.close_time)}`).getTime();
    if (newStart.getTime() < openMs || newEnd.getTime() > closeMs) {
      const fmt = (t: string) => {
        const [h, m] = t.split(':');
        const hi = Number(h);
        const ampm = hi >= 12 ? 'pm' : 'am';
        const h12 = hi % 12 || 12;
        return `${h12}:${m}${ampm}`;
      };
      return { kind: 'outside', openLabel: fmt(row.open_time), closeLabel: fmt(row.close_time) };
    }
    return null;
  })();

  // Roster validation: the chosen barber must have a shift on the
  // chosen day, AND the proposed slot must fit inside one of their
  // shifts. Same enforcement the calendar already applies to new
  // bookings — without it staff can reschedule onto a day the barber
  // doesn't work, which then breaks payroll and customer-facing
  // availability.
  type RosterIssue = { kind: 'not-rostered' } | { kind: 'outside-shift' };
  const rosterIssue: RosterIssue | null = (() => {
    if (invalidDate || !shiftsLoaded || shifts.length === 0) return null;
    const myShifts = shifts.filter((s: any) => Number(s.barber_id) === Number(barberId));
    if (myShifts.length === 0) return { kind: 'not-rostered' };
    const fits = myShifts.some((s: any) => {
      const shiftStart = new Date(s.start_time).getTime();
      const shiftEnd = new Date(s.end_time).getTime();
      return newStart.getTime() >= shiftStart && newEnd.getTime() <= shiftEnd;
    });
    if (!fits) return { kind: 'outside-shift' };
    return null;
  })();

  // Conflict detection: any other BOOKED appointment for the chosen
  // barber that overlaps [newStart, newEnd).
  const conflict = !invalidDate
    ? allBookings.find((b) => {
        if (b.barber_id !== barberId) return false;
        if (b.id === booking.id) return false;
        const s = new Date(b.start_time).getTime();
        const e = new Date(b.end_time).getTime();
        return s < newEnd.getTime() && e > newStart.getTime();
      })
    : null;

  const unchanged =
    !invalidDate && barberId === booking.barber_id && newStart.getTime() === currentStart.getTime();

  // Single source of truth for "can we submit?" — used by both the
  // button's disabled prop and the click handler. Includes a hard block
  // while shifts are still loading or no one is rostered, otherwise a
  // user could double-click before the dropdown updates.
  const blockSubmit =
    invalidDate ||
    unchanged ||
    !!openingIssue ||
    !!rosterIssue ||
    !!conflict ||
    !shiftsLoaded ||
    eligibleBarbers.length === 0;

  function handleSubmit() {
    if (blockSubmit) return;
    onConfirm(newStart.toISOString(), newEnd.toISOString(), barberId);
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '16px',
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '420px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>Reschedule appointment</h3>
        <div
          style={{
            color: 'var(--color-text-muted)',
            fontSize: '13px',
            marginBottom: '16px',
          }}
        >
          {booking.customer_name || '(no name)'} · {totalDurationMin} min
        </div>

        <label style={{ display: 'block', fontSize: '13px', marginBottom: '12px' }}>
          <span style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
            Date
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.currentTarget.value)}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid var(--color-border)',
              fontSize: '14px',
              minHeight: 44,
            }}
          />
        </label>

        <label style={{ display: 'block', fontSize: '13px', marginBottom: '12px' }}>
          <span style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
            Start time
          </span>
          <input
            type="time"
            value={time}
            step={900}
            onChange={(e) => setTime(e.currentTarget.value)}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid var(--color-border)',
              fontSize: '14px',
              minHeight: 44,
            }}
          />
        </label>

        <label style={{ display: 'block', fontSize: '13px', marginBottom: '12px' }}>
          <span style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
            Barber
          </span>
          <select
            value={eligibleBarbers.length > 0 ? barberId : ''}
            onChange={(e) => setBarberId(Number(e.currentTarget.value))}
            disabled={!shiftsLoaded || eligibleBarbers.length === 0}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid var(--color-border)',
              fontSize: '14px',
              minHeight: 44,
              backgroundColor: '#fff',
            }}
          >
            {!shiftsLoaded && <option value="">Loading rosters…</option>}
            {shiftsLoaded && eligibleBarbers.length === 0 && (
              <option value="">No barbers rostered on this day</option>
            )}
            {eligibleBarbers.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          {shiftsLoaded && eligibleBarbers.length === 0 && (
            <span
              style={{
                display: 'block',
                marginTop: '4px',
                fontSize: '12px',
                color: 'var(--color-text-muted)',
              }}
            >
              No one is rostered on this day — pick another date or add a shift first.
            </span>
          )}
          {rosterIssue && eligibleBarbers.length > 0 && (
            <span
              style={{
                display: 'block',
                marginTop: '4px',
                fontSize: '12px',
                color: 'var(--color-text-muted)',
              }}
            >
              {rosterIssue.kind === 'not-rostered'
                ? 'Not rostered on this day — pick another barber or date.'
                : 'Slot is outside this barber’s shift — pick a slot inside their hours.'}
            </span>
          )}
        </label>

        {!invalidDate && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              marginBottom: '12px',
              padding: '8px 10px',
              borderRadius: '6px',
              backgroundColor: 'rgba(0,0,0,0.03)',
            }}
          >
            New slot:{' '}
            {newStart.toLocaleString('en-AU', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              hour: 'numeric',
              minute: '2-digit',
            })}{' '}
            → {newEnd.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}
          </div>
        )}

        {conflict && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              marginBottom: '12px',
              padding: '8px 10px',
              borderRadius: '6px',
              backgroundColor: 'rgba(0,0,0,0.03)',
            }}
          >
            Slot already booked by {conflict.customer_name || 'another customer'} on this barber.
            Pick a different time.
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '9px 16px',
              borderRadius: '8px',
              border: '1px solid var(--color-border)',
              backgroundColor: '#fff',
              color: 'var(--color-text-muted)',
              fontSize: '13px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              minHeight: 44,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || blockSubmit}
            style={{
              padding: '9px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'var(--color-accent)',
              color: '#1e1e1e',
              fontWeight: 600,
              fontSize: '13px',
              cursor: submitting || blockSubmit ? 'not-allowed' : 'pointer',
              opacity: submitting || blockSubmit ? 0.5 : 1,
              minHeight: 44,
            }}
          >
            {submitting ? 'Saving…' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── New Booking Modal ─── */
function NewBookingModal({
  barberId: initialBarberId,
  barberName: initialBarberName,
  locationId,
  date,
  time,
  services,
  barbers,
  isProductOnly,
  onClose,
  onSave,
  saving,
  error,
}: {
  barberId: number;
  barberName: string;
  locationId: number;
  date: string;
  time: string;
  services: any[];
  barbers: any[];
  isProductOnly: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  saving: boolean;
  error?: string;
}) {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [selectedBarberId, setSelectedBarberId] = useState(initialBarberId);
  const selectedBarberName =
    barbers.find((b: any) => b.id === selectedBarberId)?.name || initialBarberName;
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const [searchFilter, setSearchFilter] = useState('');

  // Customer autocomplete
  const [customerSuggestions, setCustomerSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [activeSearchField, setActiveSearchField] = useState<'name' | 'email' | 'phone' | null>(
    null,
  );
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const reliabilityFor = useCustomerReliability([customerPhone]);

  // Booking someone in for later needs their mobile (backend createBooking
  // enforces the same rule); a walk-in being served now can skip it.
  const bookedAhead =
    !isProductOnly && new Date(brisbaneDateToISO(date, time)).getTime() > Date.now() + 30 * 60_000;
  const mobileMissing = bookedAhead && !isAuMobile(customerPhone);
  const customerWarning = reliabilityFor(customerPhone);

  useEffect(() => {
    if (selectedCustomerId) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const searchTerm =
      activeSearchField === 'email'
        ? customerEmail.trim()
        : activeSearchField === 'phone'
          ? customerPhone.trim()
          : customerName.trim();
    if (searchTerm.length < 2) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(() => {
      api
        .getContacts({ search: searchTerm, limit: 8 })
        .then((res) => {
          setCustomerSuggestions(res.contacts || []);
          setShowSuggestions((res.contacts || []).length > 0);
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [customerName, customerEmail, customerPhone, selectedCustomerId, activeSearchField]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(target) &&
        !(nameInputRef.current && nameInputRef.current.contains(target)) &&
        !(emailInputRef.current && emailInputRef.current.contains(target)) &&
        !(phoneInputRef.current && phoneInputRef.current.contains(target))
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectCustomerSuggestion = (c: any) => {
    setCustomerName(c.full_name || '');
    setCustomerEmail(c.email || '');
    setCustomerPhone(c.mobile || c.phone || '');
    setSelectedCustomerId(c.id);
    setActiveSearchField(null);
    setShowSuggestions(false);
  };

  const renderCustomerDropdown = () => {
    if (!showSuggestions || customerSuggestions.length === 0) return null;
    return (
      <div
        ref={suggestionsRef}
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid var(--color-border, #ddd)',
          borderRadius: '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 20,
          maxHeight: '220px',
          overflowY: 'auto',
        }}
      >
        {customerSuggestions.map((c) => (
          <div
            key={c.id}
            onClick={() => selectCustomerSuggestion(c)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              fontSize: '13px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(217,178,86,0.08)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ fontWeight: '600' }}>{c.full_name || 'Unknown'}</div>
            <div style={{ fontSize: '11px', opacity: 0.6 }}>
              {[c.email, c.mobile || c.phone].filter(Boolean).join(' · ') || 'No contact info'}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const filteredServices = services.filter((s: any) =>
    s.name.toLowerCase().includes(searchFilter.toLowerCase()),
  );

  const totalDuration = selectedServices.reduce((sum, sid) => {
    const svc = services.find((s: any) => s.id === sid);
    return sum + (svc?.duration_minutes || 0);
  }, 0);

  const totalPrice = selectedServices.reduce((sum, sid) => {
    const svc = services.find((s: any) => s.id === sid);
    return sum + (Number(svc?.price) || 0);
  }, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Use helper to convert Brisbane local time to UTC ISO string
    const startTime = new Date(brisbaneDateToISO(date, time));
    const duration = isProductOnly ? 15 : totalDuration;
    const endTime = new Date(startTime.getTime() + duration * 60000);
    onSave({
      location_id: locationId,
      barber_id: selectedBarberId,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      customer_name: customerName || undefined,
      customer_email: customerEmail || undefined,
      customer_phone: customerPhone || undefined,
      is_guest: !customerEmail && !customerPhone,
      source: 'admin',
      service_ids: isProductOnly ? [] : selectedServices,
      is_product_only: isProductOnly,
    });
  };

  const addService = (id: number) => {
    setSelectedServices((prev) => [...prev, id]);
  };

  const removeService = (index: number) => {
    setSelectedServices((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          padding: 'var(--spacing-6)',
          borderRadius: 'var(--radius-xl)',
          maxWidth: '520px',
          width: '100%',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-lg)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            fontFamily: 'var(--font-family-heading)',
            fontSize: 'var(--font-size-xl)',
            marginBottom: 'var(--spacing-1)',
          }}
        >
          {isProductOnly ? '🛒 Product Sale' : 'New Booking'}
        </h2>
        <p
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {selectedBarberName} &middot; {formatDateDisplay(date)} &middot; {time}
        </p>

        <form onSubmit={handleSubmit}>
          {/* Barber picker for product-only sales */}
          {isProductOnly && barbers.length > 1 && (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <label style={labelStyle}>Barber</label>
              <select
                value={selectedBarberId}
                onChange={(e) => setSelectedBarberId(Number(e.target.value))}
                style={inputStyle}
              >
                {barbers.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Services */}
          {!isProductOnly && (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <label style={labelStyle}>Services *</label>
              <input
                type="text"
                placeholder="Search services..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{ ...inputStyle, marginBottom: 'var(--spacing-2)' }}
              />
              {/* Selected services (removable) */}
              {selectedServices.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gap: 'var(--spacing-1)',
                    marginBottom: 'var(--spacing-2)',
                  }}
                >
                  {selectedServices.map((sid, idx) => {
                    const svc = services.find((s: any) => s.id === sid);
                    if (!svc) return null;
                    return (
                      <div
                        key={`sel-${idx}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          border: '1px solid var(--color-accent)',
                          borderRadius: 'var(--radius-default)',
                          backgroundColor: 'var(--color-accent)',
                          color: '#1e1e1e',
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        <span style={{ fontWeight: '500' }}>{svc.name}</span>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}
                        >
                          <span style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                            {svc.duration_minutes}min &middot; ${Number(svc.price).toFixed(0)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeService(idx)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '16px',
                              lineHeight: 1,
                              color: '#1e1e1e',
                              padding: '0 2px',
                              fontWeight: '700',
                              opacity: 0.7,
                            }}
                            title="Remove service"
                          >
                            &times;
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <p
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '600',
                      color: 'var(--color-accent)',
                    }}
                  >
                    {totalDuration} min &middot; ${totalPrice.toFixed(0)}
                  </p>
                </div>
              )}

              {/* Available services (clickable to add) */}
              <div
                style={{
                  maxHeight: '180px',
                  overflowY: 'auto',
                  display: 'grid',
                  gap: 'var(--spacing-1)',
                }}
              >
                {filteredServices.map((svc: any) => (
                  <div
                    key={svc.id}
                    onClick={() => addService(svc.id)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-default)',
                      backgroundColor: 'var(--color-background)',
                      color: 'var(--color-text)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    <span style={{ fontWeight: '500' }}>{svc.name}</span>
                    <span style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                      {svc.duration_minutes}min &middot; ${Number(svc.price).toFixed(0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customer details */}
          <div
            style={{ display: 'grid', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}
          >
            <div style={{ position: 'relative' }}>
              <label style={labelStyle}>Name</label>
              <input
                ref={nameInputRef}
                type="text"
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  setSelectedCustomerId(null);
                  setActiveSearchField('name');
                }}
                onFocus={() => {
                  setActiveSearchField('name');
                  if (customerSuggestions.length > 0) setShowSuggestions(true);
                }}
                placeholder="Search customers..."
                style={inputStyle}
                autoComplete="off"
              />
              {activeSearchField === 'name' && renderCustomerDropdown()}
            </div>
            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-2)' }}
            >
              <div style={{ position: 'relative' }}>
                <label style={labelStyle}>Email</label>
                <input
                  ref={emailInputRef}
                  type="email"
                  value={customerEmail}
                  onChange={(e) => {
                    setCustomerEmail(e.target.value);
                    setSelectedCustomerId(null);
                    setActiveSearchField('email');
                  }}
                  onFocus={() => {
                    setActiveSearchField('email');
                    if (customerSuggestions.length > 0) setShowSuggestions(true);
                  }}
                  placeholder="email@example.com"
                  style={inputStyle}
                  autoComplete="off"
                />
                {activeSearchField === 'email' && renderCustomerDropdown()}
              </div>
              <div style={{ position: 'relative' }}>
                <label style={labelStyle}>{bookedAhead ? 'Mobile *' : 'Mobile'}</label>
                <input
                  ref={phoneInputRef}
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => {
                    setCustomerPhone(e.target.value);
                    setSelectedCustomerId(null);
                    setActiveSearchField('phone');
                  }}
                  onFocus={() => {
                    setActiveSearchField('phone');
                    if (customerSuggestions.length > 0) setShowSuggestions(true);
                  }}
                  placeholder="04XX XXX XXX"
                  style={inputStyle}
                  autoComplete="off"
                />
                {activeSearchField === 'phone' && renderCustomerDropdown()}
              </div>
            </div>
            {mobileMissing && (
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  margin: 0,
                }}
              >
                {customerPhone.trim()
                  ? 'That doesn’t look like a mobile — use 04xx xxx xxx.'
                  : 'Needed when booking someone in for later — it sends their reminder text. A walk-in being served now can skip it.'}
              </p>
            )}
            {customerWarning && (
              <div
                role="status"
                style={{
                  fontSize: 'var(--font-size-sm)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderRadius: 'var(--radius-default)',
                  backgroundColor: 'rgba(255,193,7,0.15)',
                  border: '1px solid rgba(255,193,7,0.6)',
                }}
              >
                ⚠ {reliabilityText(customerWarning)} — worth confirming with them before the day.
              </div>
            )}
          </div>

          {error && (
            <p
              style={{
                color: 'var(--color-error)',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--spacing-3)',
                padding: 'var(--spacing-2)',
                backgroundColor: 'rgba(220,53,69,0.08)',
                borderRadius: 'var(--radius-default)',
              }}
            >
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="submit"
              disabled={
                saving || mobileMissing || (!isProductOnly && selectedServices.length === 0)
              }
              style={{
                ...btnAccent,
                flex: 1,
                opacity:
                  saving || mobileMissing || (!isProductOnly && selectedServices.length === 0)
                    ? 0.5
                    : 1,
              }}
            >
              {saving ? 'Saving...' : isProductOnly ? '🛒 Create Product Sale' : 'Create Booking'}
            </button>
            <button type="button" onClick={onClose} style={{ ...btnStyle, flex: 0 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Product Sale Modal ─────────────────────────────────────────────────────
 * Standalone POS-style modal for selling products without an appointment.
 * Steps:
 *   1. Search & select products from Square catalog
 *   2. Pick barber (who made the sale), optional customer
 *   3. Pay with Cash or Card
 * On payment: creates a product-only booking, attaches checkout_items,
 * then completes the booking (cash) or initiates terminal checkout (card).
 * ──────────────────────────────────────────────────────────────────────────── */
type ProductSaleStep = 'products' | 'processing' | 'terminal';

function ProductSaleModal({
  barbers,
  locationId,
  date,
  onClose,
  queryClient,
}: {
  barbers: any[];
  locationId: number;
  date: string;
  onClose: () => void;
  queryClient: any;
}) {
  const [step, setStep] = useState<ProductSaleStep>('products');
  const [selectedBarberId, setSelectedBarberId] = useState(barbers[0]?.id || 0);
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [showAddProducts, setShowAddProducts] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState<
    {
      id: string;
      variationId: string;
      name: string;
      variationName: string;
      price: number;
      qty: number;
    }[]
  >([]);
  const [error, setError] = useState('');
  const [createdBooking, setCreatedBooking] = useState<any>(null);

  // Load catalog items on mount
  useEffect(() => {
    api
      .getCatalogItems()
      .then((res: any) => setCatalogItems(res.items || []))
      .catch(() => setCatalogItems([]))
      .finally(() => setCatalogLoading(false));
  }, []);

  const totalPrice = selectedProducts.reduce((sum, p) => sum + p.price * p.qty, 0);

  const addProduct = (item: any, variation: any) => {
    setSelectedProducts((prev) => {
      const existing = prev.findIndex((p) => p.variationId === variation.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], qty: updated[existing].qty + 1 };
        return updated;
      }
      return [
        ...prev,
        {
          id: item.id,
          variationId: variation.id,
          name: item.name,
          variationName: variation.name || 'Regular',
          price: variation.priceCents ? variation.priceCents / 100 : 0,
          qty: 1,
        },
      ];
    });
  };

  const removeProduct = (idx: number) => {
    setSelectedProducts((prev) => {
      const updated = [...prev];
      if (updated[idx].qty > 1) {
        updated[idx] = { ...updated[idx], qty: updated[idx].qty - 1 };
      } else {
        updated.splice(idx, 1);
      }
      return updated;
    });
  };

  const createProductBooking = async () => {
    const now = new Date();
    const hh = now
      .toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', hour: '2-digit', hour12: false })
      .padStart(2, '0');
    const mm = String(Math.floor(now.getMinutes() / 15) * 15).padStart(2, '0');
    const startTime = new Date(brisbaneDateToISO(date, `${hh}:${mm}`));
    const endTime = new Date(startTime.getTime() + 15 * 60000);

    const booking = await api.createBooking({
      location_id: locationId,
      barber_id: selectedBarberId,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      customer_name: undefined,
      customer_email: undefined,
      customer_phone: undefined,
      is_guest: true,
      source: 'admin',
      service_ids: [],
      is_product_only: true,
    });

    // Attach checkout items
    await api.updateBooking(booking.id, { checkout_items: selectedProducts });
    return booking;
  };

  const handleCash = async () => {
    setStep('processing');
    setError('');
    try {
      const booking = await createProductBooking();
      await api.completeBooking(booking.id, { paymentMethod: 'cash' });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['performance-report'] });
      queryClient.invalidateQueries({ queryKey: ['financial-report'] });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to process sale');
      setStep('products');
    }
  };

  const handleCard = async () => {
    setStep('processing');
    setError('');
    try {
      const booking = await createProductBooking();
      setCreatedBooking(booking);
      setStep('terminal');
    } catch (err: any) {
      setError(err.message || 'Failed to create sale');
      setStep('products');
    }
  };

  const handleTerminalComplete = (paymentId?: string) => {
    if (createdBooking) {
      api
        .completeBooking(createdBooking.id, { paymentMethod: 'card', squarePaymentId: paymentId })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['bookings'] });
          queryClient.invalidateQueries({ queryKey: ['performance-report'] });
          queryClient.invalidateQueries({ queryKey: ['financial-report'] });
          onClose();
        })
        .catch(() => {
          onClose();
        });
    }
  };

  const filteredItems = catalogItems.filter((item: any) =>
    item.name.toLowerCase().includes(productSearch.toLowerCase()),
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          width: '100%',
          maxWidth: '540px',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Dark Header ── */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
            padding: '22px 28px',
            position: 'relative',
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: 'rgba(255,255,255,0.6)',
              transition: 'all 0.2s',
            }}
            aria-label="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
            }}
          >
            &times;
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #D9B256, #c4a03a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '700',
                color: '#1e1e1e',
              }}
            >
              🛒
            </div>
            <div>
              <div style={{ color: '#fff', fontSize: '16px', fontWeight: '600', lineHeight: 1.2 }}>
                Product Sale
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '2px' }}>
                with {barbers.find((b: any) => b.id === selectedBarberId)?.name || 'Barber'}
              </div>
            </div>
          </div>

          <div
            style={{
              color: '#D9B256',
              fontSize: '20px',
              fontWeight: '700',
              fontFamily: 'var(--font-family-heading)',
            }}
          >
            {totalPrice > 0 ? `$${totalPrice.toFixed(2)}` : 'No items yet'}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
          {step === 'processing' && (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
                Processing sale...
              </p>
            </div>
          )}

          {step === 'terminal' && createdBooking && (
            <TerminalCheckoutFlow
              booking={createdBooking}
              totalPrice={totalPrice}
              checkoutItems={selectedProducts}
              onComplete={handleTerminalComplete}
              onBack={() => setStep('products')}
            />
          )}

          {step === 'products' && (
            <>
              {/* Barber picker */}
              {barbers.length > 1 && (
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase' as const,
                        letterSpacing: '0.06em',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      Barber
                    </div>
                  </div>
                  <select
                    value={selectedBarberId}
                    onChange={(e) => setSelectedBarberId(Number(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      backgroundColor: '#fff',
                      color: '#1e1e1e',
                      outline: 'none',
                      boxSizing: 'border-box' as const,
                    }}
                  >
                    {barbers.map((b: any) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Products — same pattern as BookingDetailModal */}
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase' as const,
                      letterSpacing: '0.06em',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    Products
                  </div>
                  <button
                    onClick={() => {
                      if (showAddProducts) {
                        setShowAddProducts(false);
                        setProductSearch('');
                      } else {
                        setShowAddProducts(true);
                        setProductSearch('');
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#D9B256',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(217,178,86,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'none';
                    }}
                  >
                    {showAddProducts ? 'Done' : '+ Add Product'}
                  </button>
                </div>

                {/* Product list with inline remove */}
                {selectedProducts.length > 0 && (
                  <div
                    style={{
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    {selectedProducts.map((item, idx) => (
                      <div
                        key={`item-${idx}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 14px',
                          fontSize: '13px',
                          backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                          borderTop: idx > 0 ? '1px solid var(--color-border-light)' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => removeProduct(idx)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              lineHeight: 1,
                              color: '#ccc',
                              padding: '2px',
                              transition: 'color 0.15s',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#dc3545';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#ccc';
                            }}
                            title="Remove product"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                          <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>
                            {item.name}
                            {item.variationName !== 'Regular' && item.variationName !== item.name
                              ? ` (${item.variationName})`
                              : ''}
                          </span>
                          {item.qty > 1 && (
                            <span style={{ color: '#D9B256', fontWeight: '600', fontSize: '12px' }}>
                              x{item.qty}
                            </span>
                          )}
                        </div>
                        <span style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                          ${(item.price * item.qty).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Product picker */}
                {showAddProducts && (
                  <div style={{ marginTop: '8px' }}>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '7px 10px',
                        fontSize: '13px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '6px',
                        marginBottom: '6px',
                        outline: 'none',
                        boxSizing: 'border-box' as const,
                      }}
                    />
                    <div
                      style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px',
                      }}
                    >
                      {catalogLoading ? (
                        <div
                          style={{
                            padding: '12px',
                            fontSize: '12px',
                            color: 'var(--color-text-muted)',
                            textAlign: 'center',
                          }}
                        >
                          Loading products...
                        </div>
                      ) : filteredItems.length === 0 ? (
                        <div
                          style={{
                            padding: '12px',
                            fontSize: '12px',
                            color: 'var(--color-text-muted)',
                            textAlign: 'center',
                          }}
                        >
                          {productSearch
                            ? `No products match "${productSearch}"`
                            : 'No products in catalog'}
                        </div>
                      ) : (
                        filteredItems.map((item: any) =>
                          item.variations.map((v: any) => (
                            <div
                              key={v.id}
                              onClick={() => addProduct(item, v)}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                border: '1px solid var(--color-border-light)',
                                fontSize: '13px',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--color-surface-elevated)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span>
                                {item.name}
                                {v.name !== 'Regular' && v.name !== item.name && (
                                  <span
                                    style={{
                                      color: 'var(--color-text-muted)',
                                      marginLeft: 4,
                                      fontSize: '12px',
                                    }}
                                  >
                                    ({v.name})
                                  </span>
                                )}
                              </span>
                              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                                ${(v.priceCents / 100).toFixed(2)}
                              </span>
                            </div>
                          )),
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Total row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  fontSize: '13px',
                  backgroundColor: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '8px',
                  marginBottom: '16px',
                }}
              >
                <span style={{ fontWeight: '600' }}>Total</span>
                <span style={{ fontWeight: '700', color: '#D9B256', fontSize: '15px' }}>
                  ${totalPrice.toFixed(2)}
                </span>
              </div>

              {/* Error */}
              {error && (
                <p
                  style={{
                    color: 'var(--color-error)',
                    fontSize: '13px',
                    marginBottom: '12px',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(220,53,69,0.08)',
                    borderRadius: '8px',
                  }}
                >
                  {error}
                </p>
              )}

              {/* Payment buttons — same style as BookingDetailModal */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleCard}
                    disabled={selectedProducts.length === 0}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      borderRadius: '8px',
                      border: 'none',
                      background:
                        selectedProducts.length === 0
                          ? '#e0e0e0'
                          : 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                      color: selectedProducts.length === 0 ? '#999' : '#D9B256',
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: selectedProducts.length === 0 ? 'not-allowed' : 'pointer',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedProducts.length > 0) e.currentTarget.style.opacity = '0.85';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    💳 Card
                  </button>
                  <button
                    onClick={handleCash}
                    disabled={selectedProducts.length === 0}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      borderRadius: '8px',
                      border: 'none',
                      background:
                        selectedProducts.length === 0
                          ? '#e0e0e0'
                          : 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                      color: selectedProducts.length === 0 ? '#999' : '#D9B256',
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: selectedProducts.length === 0 ? 'not-allowed' : 'pointer',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedProducts.length > 0) e.currentTarget.style.opacity = '0.85';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    💵 Cash
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Terminal Checkout Flow ─────────────────────────────────────────────────
 * Rendered inside BookingDetailModal when the barber clicks "💳 Card".
 * Steps:
 *   1. select — pick a Square Terminal device
 *   2. waiting — polling Square every 2s for payment confirmation
 *   3. error   — something went wrong, show message + retry
 * On COMPLETED the booking is marked complete with the Square payment ID.
 * ──────────────────────────────────────────────────────────────────────────── */
type CheckoutStep = 'select' | 'waiting' | 'error' | 'pairing';

function TerminalCheckoutFlow({
  booking,
  totalPrice,
  checkoutItems,
  combinedBookingIds,
  discount,
  onComplete,
  onBack,
}: {
  booking: Booking;
  totalPrice: number;
  checkoutItems: {
    id: string;
    variationId: string;
    name: string;
    variationName: string;
    price: number;
    qty: number;
  }[];
  combinedBookingIds?: string[];
  /** Loyalty card / Voucher Discount — the backend charges what's left after it. */
  discount?: DiscountChoice | null;
  onComplete: (paymentId?: string) => void;
  onBack: () => void;
}) {
  const [step, setStep] = useState<CheckoutStep>('select');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_devices, setDevices] = useState<any[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Pairing state
  const [pairingCode, setPairingCode] = useState('');
  const [pairingCodeId, setPairingCodeId] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_pairingLoading, setPairingLoading] = useState(false);
  const [allowTip, setAllowTip] = useState(false);

  // Load available Square terminal devices for this booking's location
  useEffect(() => {
    api
      .getSquareDevices(booking.location_id)
      .then((result: any) => {
        const devs: any[] = result.devices || [];
        setDevices(devs);
        if (devs.length > 0) setDeviceId(devs[0].id);
      })
      .catch(() => setDevices([]))
      .finally(() => setDevicesLoading(false));
  }, [booking.location_id]);

  // Poll for checkout completion every 2 seconds while in 'waiting' step.
  // Note: the backend webhook and hourly reconciliation cron will auto-complete
  // any booking whose terminal payment succeeds even if polling stops, so a
  // closed modal is no longer catastrophic — but we still warn the admin.
  const onCompleteRef = React.useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (step !== 'waiting' || !checkoutId) return;
    let cancelled = false;
    let failureCount = 0;

    const poll = async () => {
      try {
        const result: any = await api.getTerminalCheckoutStatus(checkoutId);
        failureCount = 0;
        if (cancelled) return;
        if (result.status === 'COMPLETED') {
          onCompleteRef.current(result.paymentIds?.[0]);
        } else if (result.status === 'CANCELLED' || result.status === 'CANCEL_REQUESTED') {
          setStep('error');
          setErrorMsg('Payment was cancelled on the terminal. Please try again.');
        }
        // PENDING | IN_PROGRESS → keep polling
      } catch {
        failureCount += 1;
        // Tolerate a few transient failures before bailing out — network
        // blips shouldn't abandon the checkout.
        if (!cancelled && failureCount >= 5) {
          setStep('error');
          setErrorMsg(
            'Lost connection to Square. If the customer has already paid, the booking will auto-complete within the hour.',
          );
        }
      }
    };

    const interval = setInterval(poll, 2000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [step, checkoutId]);

  // While a terminal checkout is in flight, warn if the admin tries to close
  // the tab or navigate away. Not a hard block — Chrome shows the generic
  // "Leave site?" dialog. Server-side webhook + reconciliation cron will
  // still complete the booking if they leave anyway.
  useEffect(() => {
    if (step !== 'waiting' || !checkoutId) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Required by Chrome
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step, checkoutId]);

  const handleSendToTerminal = useCallback(async () => {
    if (!deviceId) return;
    setSending(true);
    try {
      let result: any;
      if (combinedBookingIds && combinedBookingIds.length > 0) {
        // Combined checkout — multiple bookings in one transaction
        const allIds = [String(booking.id), ...combinedBookingIds];
        result = await api.combinedCheckout(allIds, deviceId, allowTip);
      } else {
        result = await api.initiateTerminalCheckout(
          String(booking.id),
          deviceId,
          checkoutItems,
          allowTip,
          discount ?? null,
        );
      }
      setCheckoutId(result.checkoutId);
      setStep('waiting');
    } catch (err: any) {
      setStep('error');
      setErrorMsg(
        err.message || 'Failed to connect to the terminal. Please check your Square setup.',
      );
    } finally {
      setSending(false);
    }
  }, [booking.id, deviceId, checkoutItems, allowTip, combinedBookingIds, discount]);

  const handleCancelCheckout = useCallback(async () => {
    // If a terminal checkout is live, confirm before abandoning — the customer
    // may have already tapped the card. Backend webhook will still catch it,
    // but we want the admin to be aware.
    if (checkoutId && step === 'waiting') {
      const ok = window.confirm(
        'A payment is in progress on the terminal. If you close now and the customer has already paid, the booking will still auto-complete within the hour. Close anyway?',
      );
      if (!ok) return;
      try {
        await api.cancelTerminalCheckout(checkoutId);
      } catch {
        /* ignore */
      }
    } else if (checkoutId) {
      try {
        await api.cancelTerminalCheckout(checkoutId);
      } catch {
        /* ignore */
      }
    }
    onBack();
  }, [checkoutId, step, onBack]);

  // Handle pairing: request a device code from Square
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleStartPairing = useCallback(async () => {
    setPairingLoading(true);
    try {
      const result = await api.createDeviceCode(booking.location_id);
      setPairingCode(result.code);
      setPairingCodeId(result.id);
      setStep('pairing');
    } catch (err: any) {
      setStep('error');
      setErrorMsg(err.message || 'Failed to generate pairing code.');
    } finally {
      setPairingLoading(false);
    }
  }, [booking.location_id]);

  // Poll for pairing completion every 3 seconds while in 'pairing' step
  useEffect(() => {
    if (step !== 'pairing' || !pairingCodeId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const result = await api.getDeviceCodeStatus(pairingCodeId);
        if (cancelled) return;
        if (result.status === 'PAIRED') {
          // Paired! Refresh device list and go back to select step
          const devResult = await api.getSquareDevices(booking.location_id);
          const devs = devResult.devices || [];
          setDevices(devs);
          if (devs.length > 0) setDeviceId(devs[0].id);
          setPairingCode('');
          setPairingCodeId('');
          setStep('select');
        } else if (result.status === 'EXPIRED') {
          setStep('error');
          setErrorMsg('Pairing code expired. Please try again.');
        }
        // UNPAIRED → keep polling
      } catch {
        if (!cancelled) {
          setStep('error');
          setErrorMsg('Lost connection while checking pairing status.');
        }
      }
    };

    const interval = setInterval(poll, 3000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [step, pairingCodeId]);

  // ── Pairing step ──
  if (step === 'pairing') {
    return (
      <div style={{ padding: '8px 0', textAlign: 'center' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>📱</div>
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--color-text)',
            marginBottom: '6px',
          }}
        >
          Pair Your Square Terminal
        </div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            marginBottom: '16px',
            lineHeight: 1.5,
          }}
        >
          On your Square Terminal device, go to
          <br />
          <strong>Sign in → Use a Device code</strong>
          <br />
          and enter this code:
        </div>
        <div
          style={{
            fontSize: '32px',
            fontWeight: '800',
            letterSpacing: '0.15em',
            color: '#D9B256',
            fontFamily: 'var(--font-family-heading)',
            padding: '14px 0',
            marginBottom: '16px',
            background: 'rgba(217,178,86,0.08)',
            borderRadius: '10px',
          }}
        >
          {pairingCode}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              width: '16px',
              height: '16px',
              border: '2px solid rgba(217,178,86,0.25)',
              borderTopColor: '#D9B256',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            Waiting for terminal to pair...
          </span>
        </div>
        <button
          onClick={() => {
            setStep('select');
            setPairingCode('');
            setPairingCodeId('');
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            color: 'var(--color-text-muted)',
          }}
        >
          Cancel
        </button>
      </div>
    );
  }

  // ── Waiting for payment ──
  if (step === 'waiting') {
    return (
      <div style={{ padding: '8px 0', textAlign: 'center' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>💳</div>
        <div
          style={{
            fontWeight: '700',
            fontSize: '22px',
            color: '#D9B256',
            fontFamily: 'var(--font-family-heading)',
            marginBottom: '4px',
          }}
        >
          ${totalPrice.toFixed(2)} AUD
        </div>
        <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '18px' }}>
          Waiting for customer to pay on the terminal...
        </div>
        {/* Spinner */}
        <div
          style={{
            display: 'inline-block',
            width: '28px',
            height: '28px',
            border: '3px solid rgba(217,178,86,0.25)',
            borderTopColor: '#D9B256',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            marginBottom: '18px',
          }}
        />
        <br />
        <button
          onClick={handleCancelCheckout}
          style={{
            padding: '8px 20px',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            color: 'var(--color-text-muted)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#dc3545';
            e.currentTarget.style.color = '#dc3545';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border)';
            e.currentTarget.style.color = 'var(--color-text-muted)';
          }}
        >
          Cancel Payment Request
        </button>
      </div>
    );
  }

  // ── Error ──
  if (step === 'error') {
    return (
      <div style={{ padding: '8px 0', textAlign: 'center' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>⚠️</div>
        <div style={{ fontSize: '13px', color: '#dc3545', marginBottom: '18px', lineHeight: 1.5 }}>
          {errorMsg}
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button
            onClick={() => {
              setStep('select');
              setCheckoutId(null);
              setErrorMsg('');
            }}
            style={{
              padding: '9px 18px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
              color: '#D9B256',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
          <button
            onClick={onBack}
            style={{
              padding: '9px 18px',
              borderRadius: '8px',
              border: '1px solid var(--color-border)',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              color: 'var(--color-text-muted)',
            }}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // ── Select device ──
  return (
    <div style={{ padding: '4px 0' }}>
      {/* Amount display */}
      <div
        style={{
          marginBottom: '14px',
          padding: '12px 16px',
          background: 'rgba(217,178,86,0.08)',
          borderRadius: '8px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: '11px',
            color: 'var(--color-text-muted)',
            marginBottom: '2px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Total to Charge
        </div>
        <div
          style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#D9B256',
            fontFamily: 'var(--font-family-heading)',
          }}
        >
          ${totalPrice.toFixed(2)} AUD
        </div>
      </div>

      {/* Tip toggle */}
      <div
        onClick={() => setAllowTip((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '14px',
          padding: '10px 14px',
          borderRadius: '8px',
          border: allowTip ? '1.5px solid rgba(217,178,86,0.5)' : '1.5px solid var(--color-border)',
          background: allowTip
            ? 'linear-gradient(135deg, rgba(217,178,86,0.10) 0%, rgba(217,178,86,0.04) 100%)'
            : '#fff',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          userSelect: 'none' as const,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px', lineHeight: 1 }}>💰</span>
          <div>
            <div
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: allowTip ? '#D9B256' : 'var(--color-text)',
                letterSpacing: '0.01em',
                transition: 'color 0.2s',
              }}
            >
              Enable Tipping
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '1px' }}>
              Customer can add a tip on the terminal
            </div>
          </div>
        </div>
        <div
          style={{
            width: '36px',
            height: '20px',
            borderRadius: '10px',
            background: allowTip ? 'linear-gradient(135deg, #D9B256 0%, #c9a24a 100%)' : '#d0d0d0',
            position: 'relative',
            transition: 'background 0.25s ease',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
              position: 'absolute',
              top: '2px',
              left: allowTip ? '18px' : '2px',
              transition: 'left 0.25s ease',
            }}
          />
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleSendToTerminal}
          disabled={!deviceId || sending || devicesLoading}
          style={{
            flex: 1,
            padding: '10px 0',
            borderRadius: '8px',
            border: 'none',
            background: !deviceId ? '#ccc' : 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
            color: !deviceId ? '#888' : '#D9B256',
            fontWeight: '600',
            fontSize: '13px',
            cursor: !deviceId || sending ? 'default' : 'pointer',
            opacity: sending ? 0.7 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          {sending ? 'Sending...' : '📲 Send to Terminal'}
        </button>
        <button
          onClick={onBack}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            color: 'var(--color-text-muted)',
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
}

/* ─── Catch Up Time Modal ────────────────────────────────────────────────────
 * Quick modal to block 5-15 min catch-up time for a barber.
 * Barber picks the start time and duration.
 * ──────────────────────────────────────────────────────────────────────────── */
function CatchUpTimeModal({
  barberName,
  date,
  openTime,
  closeTime,
  onClose,
  onSave,
  saving,
}: {
  barberName: string;
  date: string;
  openTime: string;
  closeTime: string;
  onClose: () => void;
  onSave: (startTime: string, minutes: number) => void;
  saving: boolean;
}) {
  const [minutes, setMinutes] = useState(5);

  // Default to current time rounded to nearest 5 min, clamped to opening hours
  const getNowTime = () => {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Brisbane',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = fmt.formatToParts(now);
    const h = parseInt(parts.find((p) => p.type === 'hour')?.value || '0');
    const m = parseInt(parts.find((p) => p.type === 'minute')?.value || '0');
    const rounded = Math.ceil(m / 5) * 5;
    const effH = rounded >= 60 ? h + 1 : h;
    const effM = rounded >= 60 ? 0 : rounded;
    const t = `${String(effH).padStart(2, '0')}:${String(effM).padStart(2, '0')}`;
    if (t < openTime) return openTime;
    if (t > closeTime) return openTime;
    return t;
  };

  const [startTime, setStartTime] = useState(getNowTime);
  const [error, setError] = useState<string | null>(null);

  // Browsers don't hard-enforce min/max on <input type="time">. Validate
  // explicitly so a barber can't be assigned catch-up outside their shift.
  const isInRange = (t: string) => t >= openTime && t <= closeTime;
  const clampToRange = (t: string) => (t < openTime ? openTime : t > closeTime ? closeTime : t);

  const handleStartTimeChange = (v: string) => {
    setStartTime(v);
    if (!v || !isInRange(v)) {
      setError(`Start time must be between ${openTime} and ${closeTime} (barber's shift).`);
    } else {
      setError(null);
    }
  };

  const handleSave = () => {
    if (!isInRange(startTime)) {
      setStartTime(clampToRange(startTime));
      setError(`Start time must be between ${openTime} and ${closeTime}. Value was clamped.`);
      return;
    }
    setError(null);
    onSave(startTime, minutes);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-default)',
          padding: 'var(--spacing-6)',
          width: 360,
          maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        <h3
          style={{
            margin: '0 0 var(--spacing-4)',
            fontSize: 'var(--font-size-lg)',
            fontWeight: 600,
          }}
        >
          ⏱️ Catch Up Time
        </h3>
        <p
          style={{
            margin: '0 0 var(--spacing-4)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Block time for <strong>{barberName}</strong> on {date}.
        </p>

        {/* Start time picker */}
        <label
          style={{
            display: 'block',
            marginBottom: 'var(--spacing-1)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
          }}
        >
          Start time
        </label>
        <input
          type="time"
          value={startTime}
          onChange={(e) => handleStartTimeChange(e.target.value)}
          min={openTime}
          max={closeTime}
          step="300"
          style={{
            width: '100%',
            padding: 'var(--spacing-2) var(--spacing-3)',
            backgroundColor: 'var(--color-bg)',
            border: `1px solid ${error ? 'var(--color-error)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius-default)',
            color: 'var(--color-text)',
            fontSize: 'var(--font-size-base)',
            marginBottom: error ? 'var(--spacing-1)' : 'var(--spacing-4)',
          }}
        />
        {error && (
          <p
            style={{
              color: 'var(--color-error)',
              fontSize: 'var(--font-size-xs)',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            {error}
          </p>
        )}
        <p
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-xs)',
            marginTop: -8,
            marginBottom: 'var(--spacing-4)',
          }}
        >
          Shift: {openTime} – {closeTime}
        </p>

        {/* Duration picker */}
        <label
          style={{
            display: 'block',
            marginBottom: 'var(--spacing-1)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
          }}
        >
          Duration
        </label>
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-2)',
            marginBottom: 'var(--spacing-5)',
          }}
        >
          {[5, 10, 15].map((m) => (
            <button
              key={m}
              onClick={() => setMinutes(m)}
              style={{
                flex: 1,
                padding: 'var(--spacing-3) var(--spacing-2)',
                borderRadius: 'var(--radius-default)',
                border:
                  minutes === m ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                background: minutes === m ? 'var(--color-accent)' : 'var(--color-bg)',
                color: minutes === m ? '#1e1e1e' : 'var(--color-text)',
                fontSize: 'var(--font-size-base)',
                fontWeight: minutes === m ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {m} min
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-default)',
              color: 'var(--color-text)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !isInRange(startTime)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-accent)',
              border: '1px solid var(--color-accent)',
              borderRadius: 'var(--radius-default)',
              color: '#1e1e1e',
              fontWeight: 600,
              cursor: saving || !isInRange(startTime) ? 'not-allowed' : 'pointer',
              opacity: saving || !isInRange(startTime) ? 0.6 : 1,
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {saving ? 'Saving...' : 'Add Catch Up'}
          </button>
        </div>
      </div>
    </div>
  );
}
